/**
 * 메일 작성 모달 — 새 메일 / 답장 / 전달 통합.
 *
 * <h3>화면 구조 (라이트 테마)</h3>
 * <pre>
 *   ┌─────────────────────────────────────────┐
 *   │ 새 메일 작성                          [✕] │
 *   ├─────────────────────────────────────────┤
 *   │ 받는이 / 참조 / 숨은참조 / 제목          │  ← MUI Autocomplete + TextField
 *   ├─────────────────────────────────────────┤
 *   │  [Tiptap 본문 편집 영역]                  │  ← 리치 에디터 (이미지·표·링크·서명 삽입)
 *   ├─────────────────────────────────────────┤
 *   │ [첨부 칩들]                               │  ← AttachmentDropzone
 *   ├─────────────────────────────────────────┤
 *   │ [📎] [💾] [✏️서명] [✕]      [   보내기   ] │
 *   └─────────────────────────────────────────┘
 * </pre>
 *
 * <h3>본문 모델</h3>
 * <ul>
 *   <li>{@code bodyHtml} — Tiptap getHTML() (저장·발송에 사용)</li>
 *   <li>{@code bodyText} — Tiptap getText() (multipart/alternative 의 plaintext 파트)</li>
 * </ul>
 *
 * <h3>외부 주소 입력</h3>
 * 자동완성(직원+개인 연락처) + freeSolo 외부 주소 입력 + Enter/콤마/세미콜론 chip 확정.
 *
 * <h3>서명·첨부</h3>
 * <ul>
 *   <li>자동 서명: 모달 마운트 시 default 서명 1개 자동 prepend</li>
 *   <li>서명 메뉴: [저장된 서명들] / 내 서명 관리 / 새 서명 만들기</li>
 *   <li>첨부: File 객체를 메모리에 들고 있다가 발송 시 multipart 로 한 번에 전송
 *       (mailApi.dispatch). 서버에 미리 mailId 를 확보하지 않는다.</li>
 *   <li>첨부 입력은 두 갈래 — 푸터 📎 버튼(파일 선택)과 <b>작성 영역 전체 드래그&드롭</b>
 *       (파일·폴더 모두. 폴더는 하위까지 재귀). 둘 다 handleAttachmentsAdded 로 모여
 *       25MB 합계 검증을 한 곳에서 받는다.</li>
 * </ul>
 *
 * <h3>창 크기 — 모달로 열고, 좁으면 넓힌다</h3>
 * <p>헤더 우측 {@code ⤢} 버튼이 Dialog 의 {@code fullScreen} 을 토글한다. 별도 라우트(페이지)를
 * 만들지 않은 이유는 <b>이 컴포넌트가 언마운트되면 안 되기 때문</b>이다 — 첨부는 서버가 아니라
 * 메모리의 {@code File} 객체로 들고 있고(발송 시 한 번에 multipart 로 나간다), 답장 인용 HTML 과
 * AI 검토 상태도 마찬가지다. 페이지 이동으로 다시 마운트되면 그게 전부 사라지고,
 * 새로고침·뒤로가기라는 소실 경로까지 새로 생긴다. 크기만 필요하다면 값을 치를 이유가 없다.
 * <p>선택은 기억하지 않는다(localStorage 미사용) — 작성창은 늘 모달로 열린다.
 *
 * <h3>초안(DRAFT)은 사용자의 행동이 있을 때만 만든다</h3>
 * <p>작성 내용은 기본적으로 이 컴포넌트의 메모리에만 있고, 발송 시 atomic 하게 한 번에 서버로 넘어간다.
 * <b>주기 자동 임시저장(10초)과 첨부 시 자동 saveDraft 는 되살리지 않는다</b> —
 * race condition · 의도치 않은 draft 누적 · 서버 부하 때문에 걷어낸 것이고 그 이유는 그대로다.
 *
 * <p>서버로 나가는 순간은 두 가지이고, <b>둘 다 사용자가 그 자리에서 고른 결과</b>다.
 * <ol>
 *   <li>푸터의 <b>'임시저장' 버튼</b> — 누른 그 순간.</li>
 *   <li><b>쓰던 내용을 두고 창을 닫을 때</b> (handleRequestClose) — ✕·ESC·배드드롭.
 *       다만 <b>자동으로 저장하지 않고 물어본다</b> — "임시저장" 을 고른 경우에만 저장한다.
 *       '폐기' 를 고르거나 발송에 성공한 경우는 묻지도 않는다.</li>
 * </ol>
 * 세션당 호출이 유한하고 {@code draftId} 를 들고 있어 재저장도 같은 행을 갱신한다 —
 * 주기 저장을 못 쓰게 만들었던 문제들이 여기선 성립하지 않는다.
 * <b>첨부는 어느 경로로도 담지 않는다</b>(handleSaveDraft 주석 참고).
 *
 * <p>닫을 때 물어볼지 말지는 {@link hasUserContent} 가 정한다. <b>기본 서명이 자동 삽입되므로 본문은
 * 열자마자 비어 있지 않다</b> — 이걸 걷어내지 않으면 창을 열었다 닫기만 해도 저장 여부를 묻는다.
 *
 * <p>저장한 초안은 임시보관함에서 '편집'으로 다시 열 수 있고, 그때 {@code initial.draftId} 로
 * 이 창에 되돌아온다.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Chip, Dialog, DialogContent, LinearProgress,
    IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import RecipientAutocomplete from "./components/contacts/RecipientAutocomplete";
import ContactGroupPickerButton from "./components/contacts/ContactGroupPickerButton";
import ActionButton from "../../component/common/ActionButton";
import CloseRoundedIcon          from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon     from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon           from "@mui/icons-material/SendRounded";
import ScheduleSendRoundedIcon   from "@mui/icons-material/ScheduleSendRounded";
import CampaignRoundedIcon       from "@mui/icons-material/CampaignRounded";
import DeleteRoundedIcon         from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon           from "@mui/icons-material/SaveRounded";
import EditNoteRoundedIcon       from "@mui/icons-material/EditNoteRounded";
import AutoAwesomeRoundedIcon    from "@mui/icons-material/AutoAwesomeRounded";
import OpenInFullRoundedIcon     from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import { formatFileSize as formatBytes } from "../../util/numberFormat";

import DOMPurify                  from "dompurify";
import TiptapBodyEditor           from "./components/compose/TiptapBodyEditor";
import AttachmentDropzone         from "./components/compose/AttachmentDropzone";
import LargeAttachmentList        from "./components/compose/LargeAttachmentList";
import { useLargeAttachments }    from "./components/compose/useLargeAttachments";
import SignaturePickerMenu        from "./components/signature/SignaturePickerMenu";
import SignatureGalleryDialog     from "./components/signature/SignatureGalleryDialog";
import SignatureEditorDialog      from "./components/signature/SignatureEditorDialog";
import { pinImageBoxes }          from "./components/signature/pinImageBoxes";
import { inlineSignatureImages }  from "./components/signature/inlineSignatureImages";
import RegisterContactsPrompt     from "./components/contacts/RegisterContactsPrompt";
import AiReviewPanel              from "./components/ai/AiReviewPanel";
import { mailApi }                from "./api/mailApi";
import { api as apiClient }       from "../../util/api";
import { ds }                     from "../../styles/designSystem";
import { notify }                 from "../../util/toast";
import { confirm }                from "../../util/confirmDialog";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ──────────────────────────────────────────────────────────────────────────
 *  HTML 헬퍼
 *  ────────────────────────────────────────────────────────────────────── */
const htmlEscape = (s) =>
    String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * originalQuote 를 본문에 끼워 넣을 형태로.
 *
 * <p>이전엔 백엔드가 plain text 로 originalQuote 를 주고 frontend 가 escape + blockquote 래핑 했음.
 * 결과: 부모 메일의 표·이미지·서식 모두 손실 (text strip).
 *
 * <p>현재는 백엔드가 HTML 형식으로 originalQuote 를 줌 (이미 styled blockquote 로 감싸짐).
 * Frontend 는 그대로 통과시키면 됨 — escape 하면 HTML 태그가 텍스트로 보이게 됨.
 *
 * <p>레거시 plain text originalQuote 호환을 위해 HTML 태그가 보이지 않으면 기존 escape 처리.
 */
const wrapQuoteAsHtml = (htmlOrText) => {
    if (!htmlOrText) return "";
    // HTML 태그가 있으면 백엔드가 이미 HTML 로 만든 것 — 그대로 사용
    if (/<\w+[\s>]/.test(htmlOrText)) return htmlOrText;
    // 레거시 plain text — escape + blockquote 로 래핑
    return `<blockquote style="border-left:3px solid #cbd5e1;margin:0;padding-left:12px;color:#64748b;white-space:pre-wrap;">${htmlEscape(htmlOrText)}</blockquote>`;
};

/**
 * 미리보기/뷰어용 HTML 정규화 — 백엔드 {@code HtmlSanitizer.normalizeForOutbound} 와 거의 동일한 작업을
 * 프런트에서 실행. 발송 후 받는 쪽 모양과 미리보기 모양을 1:1 일치시킴.
 *
 * <h4>처리 항목</h4>
 * <ul>
 *   <li>빈 {@code <p></p>} → {@code <p><br></p>} (한 줄 빈 줄 시각적 보존)</li>
 *   <li>{@code <table>} 에 width 인라인 스타일 없으면 {@code width:100%} 부여 (collapse 방지)</li>
 *   <li>{@code <table>} 에 {@code border-collapse: collapse} 부여 (셀 사이 이중선 제거)</li>
 *   <li>{@code <td colwidth="N">} → {@code style="width:Npx"} 보강 (Tiptap 비표준 → 표준)</li>
 *   <li>{@code <col style="width:Npx">} → {@code width="N"} 속성 보강 (Outlook 호환)</li>
 *   <li>{@code <td>/<th>} 에 inline 테두리/패딩이 없으면 작성기에서 본 모양과 동일한 기본값 부여</li>
 * </ul>
 *
 * <h4>서명({@code [data-signature]}) 은 건드리지 않는다</h4>
 * <p>위 보정은 전부 "사용자가 표 삽입 버튼으로 만든 데이터 표" 를 전제로 한다. 명함형 서명은
 * 표를 레이아웃 도구로 쓰기 때문에(테두리 없음 + 밑줄 한 줄) 같은 보정을 적용하면 디자인이 부서진다.
 * 서명은 이미 완성된 결과물이므로 원본 그대로 통과시킨다.
 *
 * <h4>왜 프런트에서도 하나</h4>
 * <p>백엔드 normalize 는 발송 시점에만 동작. 미리보기는 발송 전 단계라 백엔드 통과를 안 함.
 * 미리보기가 "받는 사람 화면과 일치한다" 는 약속을 지키려면 같은 정규화가 필수.
 *
 * <h4>DOMParser 사용 이유</h4>
 * <p>regex 로 표 속성 처리하면 nested 표·escape 등에서 깨짐. 브라우저 내장 HTML 파서가 가장 안전.
 */
function normalizeBodyForPreview(html) {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, "text/html");
    const root = doc.getElementById("__root");
    if (!root) return html;

    // 1. 빈 <p> → <p><br></p>
    root.querySelectorAll("p").forEach(p => {
        const empty = p.childNodes.length === 0
            || (p.children.length === 0 && p.textContent.replace(/ /g, "").trim() === "");
        if (empty) p.innerHTML = "<br>";
    });

    // 2. <table> width / border-collapse / border 강제
    //    - 모든 셀이 검정 보더를 갖고 + border-collapse: collapse 면 표 외곽도 자연스럽게 검정 1px.
    //    - <table border="1"> attribute 는 옛 Outlook 호환용 (style 무시하는 클라이언트 대비).
    root.querySelectorAll("table").forEach(t => {
        if (t.closest("[data-signature]")) return; // 서명 표는 손대지 않는다 — 아래 4번 주석 참고
        let next = (t.getAttribute("style") || "").replace(/border\s*:[^;]*;?/gi, "").trim();
        if (next && !next.endsWith(";")) next += ";";
        if (!/border-collapse\s*:/i.test(next)) next += " border-collapse: collapse;";
        if (!/(^|;)\s*width\s*:/i.test(next)) next += " width: 100%;";
        next += " border: 1px solid #000;";
        t.setAttribute("style", next.trim());
        // attribute level — 옛 Outlook 등 style 무시하는 클라이언트 대비
        if (!t.hasAttribute("border")) t.setAttribute("border", "1");
    });

    // 3. <col style="width: Npx"> → 추가로 width="N" 속성 (Outlook 호환)
    root.querySelectorAll("colgroup > col").forEach(col => {
        if (col.hasAttribute("width")) return;
        const m = /width\s*:\s*(\d+)\s*px/i.exec(col.getAttribute("style") || "");
        if (m) col.setAttribute("width", m[1]);
    });

    // 4. td/th 의 colwidth(Tiptap 비표준) → width 속성 + style 보강 + 기본 테두리/패딩
    root.querySelectorAll("td, th").forEach(cell => {
        const cw = cell.getAttribute("colwidth");
        if (cw) {
            const first = cw.split(",")[0].trim();
            if (/^\d+$/.test(first)) {
                if (!cell.hasAttribute("width")) cell.setAttribute("width", first);
                const s = cell.getAttribute("style") || "";
                if (!/(^|;)\s*width\s*:/i.test(s)) {
                    cell.setAttribute("style",
                        s + (s && !s.endsWith(";") ? "; " : "") + `width: ${first}px;`);
                }
            }
        }
        /*
         * 서명 안의 표는 예외 — 여기서 return.
         *
         * 아래 보더 강제는 "사용자가 표 삽입 버튼으로 만든 데이터 표"를 위한 것이다.
         * 그런 표는 보더가 없으면 받는 쪽에서 칸 구분이 안 보여 못 읽는다.
         * 하지만 명함형 서명은 표를 2단 레이아웃 도구로 쓴다 — 테두리는 일부러 없고,
         * td 인라인의 border-bottom(빨간 밑줄) 하나만 살아 있어야 하는 구조다.
         *
         * 여기에 border 단축 속성을 뒤에 붙이면 앞의 border-bottom-* 개별 속성을 덮어써서
         * 빨간 밑줄이 검정으로 바뀌고, 없던 세 면에도 검정선이 생겨 격자가 된다.
         * (border-bottom-color: rgb(232,43,41) → 실제 렌더 rgb(0,0,0) 으로 확인)
         *
         * 이 함수는 미리보기뿐 아니라 실제 발송 본문에도 쓰이므로(buildPayload),
         * 예외를 안 두면 받는 사람에게도 부서진 서명이 간다.
         */
        if (cell.closest("[data-signature]")) return;

        // 셀 보더 — 항상 검정으로 강제. 외부 메일 클라이언트(Gmail/Naver/Outlook) 모두에서 명확히 보임.
        // 이전엔 if (!/border:/) 로 skip 하던 셀이 있어 흰색·invisible 보더가 그대로 발송됐음.
        // 다크 모드 대응: 받는 쪽 클라이언트 (Gmail/Naver 다크모드, Apple Mail) 가 inline 검정을 자동으로
        // 흰색 invert 처리해주는 게 표준 동작. 우리는 검정 명시만 하면 됨 (회색 #94a3b8 같은 중간색은
        // 자동 invert 알고리즘이 일관되지 않게 처리할 수 있음 — 검정·흰색이 가장 안전).
        let cellStyle = (cell.getAttribute("style") || "").replace(/border\s*:[^;]*;?/gi, "").trim();
        if (cellStyle && !cellStyle.endsWith(";")) cellStyle += ";";
        cellStyle += " border: 1px solid #000; padding: 6px 8px;";
        cell.setAttribute("style", cellStyle);
        if (cell.tagName.toLowerCase() === "th") {
            const ths = cell.getAttribute("style") || "";
            if (!/background/i.test(ths)) {
                cell.setAttribute("style",
                    ths + (ths && !ths.endsWith(";") ? "; " : "") + "background-color: #f8fafc; font-weight: 600;");
            }
        }
    });

    return root.innerHTML;
}

/* ──────────────────────────────────────────────────────────────────────────
 *  본문 빌드 헬퍼 — 새 메일/답장/전달 모두 일관된 HTML 구조로 조립.
 *
 *  서명은 <div data-signature="1"> 로 감싸서 SignatureBlock 노드(Tiptap)로 보존되게 한다.
 *  AI 검수기는 [data-signature] 안의 텍스트는 절대 건드리지 않음.
 *  ────────────────────────────────────────────────────────────────────── */
function wrapSignature(html) {
    if (!html) return "";
    // 이미 wrap 돼 있으면 그대로 (round-trip 안전)
    return /<div[^>]+data-signature/.test(html) ? html : `<div data-signature="1">${html}</div>`;
}

/*
 * 서명은 더 이상 초기 본문 HTML 에 끼워 넣지 않는다.
 *
 * (과거 구조) 여기서 signatureHtml 을 initial content 에 넣고 editorKey 를 올려 에디터를 재생성 →
 *   (1) 에디터가 빈 상태로 먼저 mount 된 뒤 서명 목록 API 응답이 와야 채워지고
 *   (2) setBodyHtml 과 setEditorKey 의 배치 타이밍/재생성 순서에 의존해
 *   서명이 조용히 누락되던 취약한 구조였다. (사용자 리포트: "서명이 올바르게 안 들어감")
 *
 * (현재) 서명 HTML 은 pendingSignatureRef 에 담아두고, 에디터가 준비된 뒤
 *   insertInitialSignature 가 문서 첫 문단 뒤(인용 앞)에 명령형으로 1회 삽입한다.
 */
function buildInitialBody({ originalQuote }) {
    const empty = "<p></p>";
    const quote = originalQuote ? wrapQuoteAsHtml(originalQuote) : "";
    return empty + quote;
}

/**
 * HTML → 평문 (multipart/alternative 의 text 파트용).
 *
 * <p>Tiptap 의 {@code getText()} 를 못 쓰는 경로에서만 쓴다 — 현재는 다시보내기.
 * regex 태그 제거 대신 DOMParser 를 쓰는 이유는 {@code <div>a</div><div>b</div>} 처럼 태그가
 * 곧 줄바꿈인 경우 regex 로는 "ab" 로 붙어버리기 때문이고, DOMParser 문서는 비활성(inert)이라
 * 이미지 로딩·스크립트 실행이 일어나지 않는다.
 */
function htmlToPlainText(html) {
    if (!html) return "";
    try {
        const doc = new DOMParser().parseFromString(html, "text/html");
        // 블록 경계를 줄바꿈으로 — 안 하면 문단이 한 줄로 뭉친다.
        doc.body.querySelectorAll("br").forEach(br => br.replaceWith("\n"));
        doc.body.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, h5, h6")
            .forEach(el => el.append("\n"));
        return (doc.body.textContent || "")
            .replace(/ /g, " ")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
    } catch {
        return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
}

/** 단체 받는이 배열(문자열 또는 {name,email})에서 유효 이메일만 중복 제거해 반환. */
function bulkEmailList(arr) {
    const seen = new Set();
    const out = [];
    for (const v of arr || []) {
        let email = typeof v === "string" ? v : (v?.email || "");
        const m = String(email).match(/<([^>]+)>/);
        if (m) email = m[1];
        email = email.trim();
        const k = email.toLowerCase();
        if (email.includes("@") && !seen.has(k)) { seen.add(k); out.push(email); }
    }
    return out;
}

/**
 * Date → datetime-local 입력이 받는 "YYYY-MM-DDTHH:mm".
 * <p>toISOString() 은 UTC 로 변환해 버려 KST 기준 시각이 9시간 어긋난다 — 반드시 로컬 필드로 조립한다.
 */
function toLocalInputValue(d) {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 예약 시각 빠른 선택 — 흔한 경우를 클릭 한 번으로. 직접 입력도 그대로 가능하다. */
const SCHEDULE_PRESETS = [
    { label: "1시간 뒤", at: () => new Date(Date.now() + 60 * 60 * 1000) },
    {
        label: "내일 오전 9시",
        at: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; },
    },
    {
        // 다음 주 월요일 오전 9시 — 오늘이 월요일이어도 '다음' 주로 간다.
        label: "다음 주 월요일 9시",
        at: () => {
            const d = new Date();
            d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
            d.setHours(9, 0, 0, 0);
            return d;
        },
    },
];

/**
 * 푸터 왼쪽 도구 버튼 — <b>아이콘 위, 라벨 아래</b> (2026-08-08 요청).
 *
 * <p>아이콘만 두면 무엇을 하는 버튼인지 마우스를 올려 봐야 안다. 이 줄에는 '폐기'(되돌릴 수 없음)와
 * '임시저장'(그 반대)이 나란히 서 있어서, 눌러 보고 알게 되는 비용이 다른 툴바보다 훨씬 크다.
 * 라벨은 상시로 붙여 두고, 툴팁은 라벨만으로 부족한 <b>단서</b>(첨부 제외 등)를 담는다.
 *
 * <h4>{@code component="span"} 을 쓸 수 있어야 한다</h4>
 * <p>첨부 버튼은 {@code <Box component="label">} 안에 들어간다(AttachmentDropzone mode="trigger").
 * 그 안에 진짜 {@code <button>} 이 있으면 클릭을 삼켜 파일 선택창이 열리지 않는다.
 *
 * <h4>disabled 여도 툴팁이 떠야 한다</h4>
 * <p>비활성 버튼은 포인터 이벤트를 받지 않아 툴팁이 안 뜬다 — 정작 "왜 못 누르는지" 를 알아야 할
 * 때가 그때다. 그래서 {@code <span>} 으로 한 겹 감싼다(MUI 권장 우회).
 */
function FooterAction({ icon, label, tooltip, color = "#64748b", disabled, ...btnProps }) {
    const button = (
        <IconButton
            size="small"
            disabled={disabled}
            sx={{
                flexDirection: "column", gap: 0.15,
                px: 1, py: 0.5, borderRadius: "8px", color,
                "&:hover": { bgcolor: "#eef2f7" },
                "&.Mui-disabled": { color: "#cbd5e1" },
            }}
            {...btnProps}
        >
            {/* 아이콘 칸 높이를 고정한다 — 글리프마다 시각적 크기가 달라 fontSize 를 18/20 으로 다르게 주는데,
                그대로 두면 버튼 높이가 2px 씩 어긋나 라벨 네 개가 계단처럼 내려앉는다. */}
            <Box sx={{ height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {icon}
            </Box>
            <Box component="span" sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {label}
            </Box>
        </IconButton>
    );
    // placement="top" — 이 줄은 모달 맨 아래라 기본값(bottom)이면 툴팁이 화면 밖으로 잘린다.
    return tooltip ? <Tooltip title={tooltip} placement="top"><span>{button}</span></Tooltip> : button;
}

/* ── 메인 ─────────────────────────────────────────────────────────── */
export default function MailWriteModal({ open, onClose, initial, onSent }) {
    const [tos, setTos]           = useState([]);
    const [ccs, setCcs]           = useState([]);
    const [bccs, setBccs]         = useState([]);
    const [subject, setSubject]   = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [editorKey, setEditorKey] = useState(0); // 모달 reopen 시 reset 트리거

    // 인용 블록 raw HTML — Tiptap schema 변환을 거치지 않고 별도 contentEditable 영역에 표시.
    // 외부 메일 (Ultralytics/혁신의숲 등) 의 복잡한 표·이미지·style 을 손실 없이 보존하면서도
    // 사용자가 native contentEditable 로 편집 가능. (Gmail/Outlook 도 동일하게 편집 허용)
    // 발송 시 quoteRef.current.innerHTML 로 사용자가 편집한 최신 상태 추출.
    const [quoteHtmlRaw, setQuoteHtmlRaw] = useState("");
    const quoteRef = useRef(null);
    // contentEditable div 에 initial HTML 박기 — 모달 reopen (editorKey 증가) 시점에만.
    // 사용자 키 입력 동안엔 ref 의 innerHTML 만 native 갱신, React state 업데이트 X → cursor 점프 0.
    // 발송 시 buildPreviewPayload 가 quoteRef.current.innerHTML 로 최신 편집 결과 추출.
    useEffect(() => {
        if (quoteRef.current) {
            quoteRef.current.innerHTML = quoteHtmlRaw || "";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorKey]);
    /**
     * 인용 영역의 <b>지금</b> HTML — state 가 아니라 DOM 이 진실이다.
     *
     * <p>사용자가 contentEditable 로 친 글자는 React state 로 올라오지 않는다(캐럿 점프를 피하려고
     * 일부러 그렇게 뒀다). 발송·AI 검토·서명 삽입처럼 "현재 내용" 이 필요한 지점은 전부 여기를 지난다.
     * 다시보내기에서는 이 영역이 본문 그 자체라 특히 중요하다.
     */
    const currentQuoteHtml = () => (quoteRef.current ? quoteRef.current.innerHTML : quoteHtmlRaw);

    const [inReplyTo, setInReplyTo]   = useState(null);
    const [references, setReferences] = useState(null);
    // 답장/전달의 원본 메일 id. prepareReply/prepareForward 응답에 들어있던 값을 그대로 되돌려보낸다.
    // 전달은 원본과 이어주는 표준 헤더가 없어 이 값이 없으면 서버가 일반 발송과 구분하지 못한다.
    const [parentMailId, setParentMailId] = useState(null);
    /*
     * 다시보내기의 원본 메일 id (prepareResend 응답). parentMailId 와 <b>따로</b> 든다 —
     * parentMailId 로 보내면 서버가 이 발송을 '전달' 로 감사 기록하고, 첨부도 복사가 아니라
     * 원본 참조로 붙는다(정반대 동작). 다시보내기는 원본과 아무 것도 공유하지 않는 새 메일이다.
     */
    const [resendSourceMailId, setResendSourceMailId] = useState(null);
    /*
     * 다시보내기 모드 — 화면 구성이 답장·전달과 갈리는 유일한 분기점이다.
     *
     * <p>답장·전달은 "위: 새로 쓸 칸(Tiptap) / 아래: 인용" 두 칸으로 나뉜다. 위 칸이 곧 사용자가
     * 쓸 자리라 비어 있어도 자리를 차지하는 게 맞다. 다시보내기는 <b>원본을 그대로 한 번 더</b>
     * 보내는 것이라 위에 쓸 내용이 없다 — 그런데도 같은 레이아웃을 쓰던 탓에 RichTextEditor 의
     * 기본 minHeight(240) + padding(32) 만큼 빈 칸이 본문 위에 얹혀 보였고, 그 빈 문단({@code <p></p>})
     * 이 실제 발송 본문 맨 위에도 한 줄로 딸려 나갔다.
     *
     * <p>그래서 다시보내기에서는 위 칸을 아예 렌더링하지 않고 <b>원본 본문(인용 영역)만</b> 남긴다.
     * 그 영역은 contentEditable 이라 그대로 편집할 수 있다. 대신 Tiptap 이 없으므로 서식 툴바도
     * 같이 사라진다 — 원본 HTML 을 schema 변환 없이 보존하는 게 다시보내기의 목적이라 이쪽을 택했다.
     * (Tiptap 에 원본을 넣으면 표·인라인 이미지·style 이 한 홉 더 가며 손상된다 — prepareResend 주석 참고.)
     */
    const isResendMode = resendSourceMailId != null;

    const [draftId, setDraftId]   = useState(null);
    const [savingDraft, setSavingDraft] = useState(false);

    /** 전체화면 여부 — 모달로 열되 좁으면 넓힌다. 세션마다 초기화(기억하지 않는다). */
    const [expanded, setExpanded] = useState(false);

    /**
     * 이번 세션에서 닫기 시 임시저장을 <b>물어보지도 않고</b> 건너뛸지 —
     * 발송 완료 / 사용자가 '폐기' 를 고른 경우. 둘 다 이미 답이 정해진 상황이라 또 묻지 않는다.
     *
     * <p>useState 가 아니라 useRef 인 이유: 발송 성공 직후 {@code onSent} 가 모달을 닫는데,
     * 그 사이 리렌더가 보장되지 않는다. 상태였다면 닫히는 시점에 옛 값(false)을 읽어
     * <b>방금 보낸 메일을 두고 "임시저장할까요?" 를 묻게 된다.</b>
     */
    const skipAutoSaveRef = useRef(false);
    /** 닫기 확인 다이얼로그가 떠 있는 동안 재진입 차단 — 사유는 {@link handleRequestClose} 주석. */
    const closePromptRef = useRef(false);
    /** 발송 진행 표시 — { label, pct }. pct 가 null 이면 불확정(서버 처리 구간). */
    const [sendPhase, setSendPhase] = useState(null);
    const [sending, setSending]   = useState(false);

    // 첨부 — { id, filename, mimeType, sizeBytes }
    const [attachments, setAttachments] = useState([]);

    // 다중 서명
    const [signatures, setSignatures] = useState([]);
    const [pickerAnchor, setPickerAnchor] = useState(null);
    const [galleryOpen, setGalleryOpen]   = useState(false);
    const [editorDlg, setEditorDlg]       = useState(null); // null 이면 닫힘, { id?, name?, bodyHtml? }

    // 발송 미리보기 — "보내기" 클릭 시 1차로 띄우는 확인 다이얼로그.
    // 사용자가 작성한 메일이 받는 이에게 어떻게 보일지 그대로 그려주고, AI 첨부 감지 결과도 같이 표시.
    // 여기서 "최종 발송" 클릭 시에만 실제 SES 발송이 일어남.
    const [previewOpen, setPreviewOpen]       = useState(false);
    const [previewPayload, setPreviewPayload] = useState(null);
    /**
     * 미리보기에만 붙이는 대용량 첨부 블록 HTML.
     *
     * ★ previewPayload.bodyHtml 에 섞으면 안 된다. 그 payload 가 그대로 발송되는데,
     *   서버도 발송 시점에 같은 블록을 주입하므로 본문에 블록이 두 번 들어간다(실측).
     *   미리보기는 '보여주기' 전용이고 발송 내용은 손대지 않는다.
     */
    const [previewLargeBlock, setPreviewLargeBlock] = useState("");
    const [aiAttachWarn, setAiAttachWarn]     = useState(null); // { mentionsAttachment, evidence: [...] } | null

    // 외부에서 Tiptap editor 핸들 보관 — 서명 insertContent 등에 사용
    const editorRef = useRef(null);
    // 이번 작성 세션에 삽입할 서명 HTML — 에디터 준비 후 insertInitialSignature 가 1회 삽입.
    const pendingSignatureRef = useRef("");
    // 서명을 이미 삽입한 editorKey — 같은 세션에서 중복 삽입 방지.
    const signatureInsertedKeyRef = useRef(null);

    /**
     * 발송 진행 중 가드 — useState 대신 useRef.
     *
     * useState 는 비동기라 같은 이벤트 핸들러 turn 안에선 새 값 못 읽음.
     * 빠른 더블클릭 (16ms 안) 에 disabled prop 이 적용되기 전 두 번째 onClick 이 통과하면
     * composeAndSend 가 두 번 호출되어 두 번째가 "DRAFT 가 아닌 메일은 수정 불가" 로 실패.
     * useRef 는 mutation 즉시 반영되므로 onClick 진입부에 ref.current 검사로 hard block 가능.
     */
    const sendingRef = useRef(false);

    // 발송 직후 새 연락처 등록 prompt
    const [registerPrompt, setRegisterPrompt] = useState(null); // null | [{name, email}, ...]

    /**
     * 사내 도메인 — 새 연락처 자동 등록 prompt 에서 제외할 도메인.
     * `mail-system.send.default-domain` 한 개를 표준으로 보고, 본인(me) 메일 도메인도 같이 사내로 취급.
     * 사내 직원과 메일 주고받을 때 매번 "새 연락처 등록?" prompt 가 뜨던 버그 방지.
     */
    const [internalDomains, setInternalDomains] = useState(() => new Set());
    useEffect(() => {
        const set = new Set();
        mailApi.getSendDomain().then(res => {
            const d = (typeof res === "string" ? res : (res?.data ?? "")).toLowerCase().trim();
            if (d) set.add(d);
            setInternalDomains(new Set(set));
        }).catch(() => {});
        // 본인 메일 도메인도 사내로 — 발신자가 다른 도메인을 쓰는 경우 보강
        apiClient.get(`${API_BASE}/employees/me`).then(me => {
            const email = (me?.email || "").toLowerCase();
            const at = email.indexOf("@");
            if (at >= 0) {
                set.add(email.slice(at + 1));
                setInternalDomains(new Set(set));
            }
        }).catch(() => {});
    }, []);

    // AI 검토 패널 — 켜지면 모달 너비 lg → xl 로 확장하고 우측에 diff 패널 표시.
    // 제목 추천은 AI 검토 패널 안에서 동시에 처리되므로 별도 state·핸들러 X.
    const [aiOpen, setAiOpen] = useState(false);

    /* 작성 모드 — 상단 공통 토글: normal(일반) / bulk(단체) / scheduled(예약).
       예약: 제목 아래 '예약 시간' 필드 노출. 단체: 받는이를 개별 발송 목록으로. */
    const [mode, setMode] = useState("normal");
    const bulkMode = mode === "bulk";
    const scheduledMode = mode === "scheduled";
    const [scheduleAt, setScheduleAt] = useState("");   // 예약 발송 시각 (datetime-local)
    // 받는이·참조·숨은참조는 세 모드가 <b>같은 state 를 공유</b>한다.
    //   [기존 문제] 단체 모드만 bulkTos 라는 별도 state 를 썼다. 그래서 일반 메일에서 받는이를 채우고
    //   '단체'로 바꾸면 입력이 통째로 사라졌다(일반↔예약 사이에선 멀쩡히 유지되는데 단체만).
    //   모드는 '어떻게 보낼지' 일 뿐 '무엇을 쓰는지' 가 아니므로, 작성 내용은 모드와 무관해야 한다.
    const bulkList = bulkEmailList(tos);                // 단체 발송 대상 — 유효·중복제거 이메일 목록

    /* ── 모달 열릴 때 초기화 ────────────────────────────── */
    useEffect(() => {
        if (!open) return;
        // 창 크기·닫기 가드는 세션마다 초기 상태로 돌린다. 안 그러면 직전 세션에서
        // 폐기를 골랐던 사실이 다음 작성까지 따라와 닫을 때 묻지도 않고 사라진다.
        setExpanded(false);
        skipAutoSaveRef.current = false;
        closePromptRef.current  = false;
        let cancelled = false;
        (async () => {
            // 서명 목록 로드 — listSignatures 결과 중 isDefault=true 행이 기본 서명.
            let sigList = [];
            let defaultHtml = "";
            try {
                const res = await mailApi.listSignatures();
                sigList = (res?.data ?? res) || [];
                const def = sigList.find(s => s.isDefault) || sigList[0];
                defaultHtml = def?.bodyHtml || "";
            } catch { /* 서명이 아직 등록 안 됐을 수도 — 비워둠 */ }
            if (cancelled) return;
            setSignatures(sigList);

            /*
             * 임시보관함에서 '편집'으로 열었나.
             *
             * 저장해 둔 본문을 그대로 되살려야 하므로 buildInitialBody(빈 본문 + 인용) 를 쓰지 않고,
             * 서명도 다시 넣지 않는다 — 저장된 본문에 이미 들어 있어서 열 때마다 한 벌씩 늘어난다.
             */
            const isDraftEdit = !!(initial && initial.draftId);

            // 답장이 응답에 signature(HTML) 를 줬다면 그걸 우선, 아니면 기본 서명(isDefault).
            const sigHtml = (initial && typeof initial.signature === "string" && initial.signature)
                ? initial.signature
                : defaultHtml;
            /*
             * 다시보내기(prepareResend) 인가 — 초안 편집과 같은 취급을 받아야 하는 지점이 둘 있다.
             *   ① 서명을 새로 넣지 않는다: 되살린 본문에 이미 그때의 서명이 들어 있어서, 넣으면
             *      다시 보낼 때마다 서명이 한 벌씩 늘어난다.
             *   ② 첨부 칩의 id 를 forwardAttachmentIds 가 아니라 resendAttachmentIds 로 보낸다
             *      (아래 setAttachments / buildPayload) — 서버 동작이 참조 vs 복사로 갈린다.
             */
            const isResend = !!(initial && initial.resend);
            // 서명은 초기 본문에 넣지 않고, 에디터 준비 후 insertInitialSignature 가 삽입한다.
            pendingSignatureRef.current = (isDraftEdit || isResend) ? "" : (sigHtml || "");

            // 폼 초기화
            if (initial) {
                setTos((initial.tos || []).map(s => s));
                setCcs((initial.ccs || []).map(s => s));
                setBccs((initial.bccs || []).map(s => s));
                setSubject(initial.subject || "");
                // quoteHtmlRaw 가 있으면 Tiptap 안에 originalQuote 안 박음 (별도 영역) — 외부 메일 복잡 HTML 손실 방지
                const useSeparateQuote = !!initial.quoteHtmlRaw;
                setBodyHtml(
                    isDraftEdit ? (initial.bodyHtml || "")   // 저장해 둔 본문 그대로
                    // 다시보내기는 위 작성 칸 자체를 렌더링하지 않는다 — buildInitialBody 의 빈 <p> 를
                    // 넣어두면 발송 본문 맨 위에 빈 줄로 남는다(원본과 같아야 한다는 전제를 깬다).
                    : isResend ? ""
                    : buildInitialBody({ originalQuote: useSeparateQuote ? null : initial.originalQuote }));
                setQuoteHtmlRaw(useSeparateQuote ? initial.quoteHtmlRaw : "");
                setInReplyTo(initial.inReplyTo || null);
                setReferences(initial.references || null);
                setParentMailId(initial.parentMailId ?? null);
                setResendSourceMailId(initial.resendSourceMailId ?? null);
            } else {
                setTos([]); setCcs([]); setBccs([]); setSubject("");
                setBodyHtml(buildInitialBody({ originalQuote: null }));
                setQuoteHtmlRaw("");
                setInReplyTo(null); setReferences(null);
                setParentMailId(null);
                setResendSourceMailId(null);
            }
            setBodyText("");
            // 편집으로 열었으면 그 초안 id 를 들고 간다 — 재저장·발송이 새 행을 만들지 않고
            // 기존 행을 갱신하도록(payload.id → 서버 saveDraft 가 갱신 경로를 탄다).
            setDraftId(initial?.draftId ?? null);
            /*
             * 전달·다시보내기면 원본 첨부를 '참조 칩' 으로 올려둔다 (initial.attachments — prepare* 응답).
             *
             * file 이 없는 항목이라 performDispatch 의 `a.file instanceof File` 필터에 안 걸리고,
             * 대신 id 만 payload 로 나간다. 브라우저가 파일을 다시 올리지 않는 건 두 경우 모두 같다.
             *
             * 여기가 곧 "가져올 첨부를 고르는 UI" 다 — 전부 올라온 상태가 기본값이고, 사용자가 칩의
             * X 로 지운 것은 payload 에 안 실린다. 다시보내기에서 첨부를 다 지우면 S3 복사가 0 건이 된다.
             *
             * resend 플래그를 칩마다 심는 이유: 서버에서 갈리는 동작이 정반대라(전달=원본 객체 참조,
             * 다시보내기=새 키로 복사) 어느 필드로 되돌려 보낼지 buildPayload 가 알아야 한다.
             *
             * 답장(prepareReply)은 attachments 를 안 내려주므로 자연히 빈 배열 — 기존 동작 그대로다.
             */
            setAttachments((initial?.attachments || []).map(a => ({
                id: `__fwd__${a.id}`,   // handleAttachmentRemove 가 id 로 지우므로 로컬 첨부와 겹치지 않게
                refId: a.id,            // ← 서버로 되돌려 보낼 mail_attachment.id
                resend: isResend,       // true 면 forwardAttachmentIds 대신 resendAttachmentIds 로
                filename: a.filename,
                sizeBytes: a.sizeBytes,
                mimeType: a.mimeType,
                file: null,             // 업로드할 File 없음 = 참조
            })));
            setMode("normal");
            setScheduleAt("");
            // 다시보내기는 RichTextEditor 를 렌더링하지 않는다 — 직전 세션의 (이미 파괴된) 에디터
            // 인스턴스가 ref 에 남아 있으면 서명 삽입·AI 반영이 없는 에디터를 잡고 조용히 실패한다.
            if (isResend) editorRef.current = null;
            setEditorKey(k => k + 1);
        })();
        return () => { cancelled = true; };
    }, [open, initial]);

    /*
     * 서명 자동 삽입 — 에디터가 이번 작성 세션용으로 (재)생성된 뒤 딱 1회 실행.
     *
     * RichTextEditor 는 editorKey 변경 시 에디터를 재생성하고 setContent 로 본문(빈 <p>)을 세팅한다.
     * 그 동기 이펙트 체인이 끝난 다음 매크로태스크에서 삽입해야 setContent 가 서명을 덮어쓰지 않는다.
     * (그래서 setTimeout 으로 한 tick 뒤 실행 — 초기 빈-mount 레이스/키입력-덮어쓰기/응답 지연 모두 회피)
     *
     * 삽입 위치: 첫 문단(빈 줄) 뒤 = 인용이 있으면 그 앞. 서명은 새 메시지 영역에 속하므로.
     * 삽입 후 캐럿을 맨 위로 되돌려 사용자가 서명 위에서 바로 작성하게 한다.
     * pendingSignatureRef 가 비어 있으면(서명 미등록) 아무것도 하지 않는다.
     */
    useEffect(() => {
        if (!open) return;
        const keyAtSchedule = editorKey;
        const t = setTimeout(async () => {
            if (signatureInsertedKeyRef.current === keyAtSchedule) return; // 이미 삽입한 세션
            const editor = editorRef.current;
            if (!editor || editor.isDestroyed) return;
            signatureInsertedKeyRef.current = keyAtSchedule;               // 재진입/중복 방지 (먼저 마킹)
            const sigHtml = pendingSignatureRef.current;
            if (!sigHtml) return;                                          // 서명 없음 → 빈 본문 유지
            let safe;
            try {
                safe = await prepareSignatureHtml(sigHtml);
            } catch { return; }
            if (editor.isDestroyed) return;
            const doc = editor.state.doc;
            const insertPos = doc.firstChild ? doc.firstChild.nodeSize : doc.content.size;
            editor.chain().insertContentAt(insertPos, safe).setTextSelection(1).focus().run();
        }, 60);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorKey, open]);

    /*
     * 자동 임시저장 제거됨 — 메모리 보유 + Atomic 발송 패턴으로 전환.
     * 사용자가 작성 중엔 서버 호출 0회. 최종 발송 시점에 모든 데이터를 한 번에 multipart 로 전송.
     * 이전엔 10초마다 saveDraft 가 fire 해서 race condition + 의도치 않은 draft 누적 + 서버 부하.
     * 초안은 명시적 사용자 액션으로만 만든다 — 푸터의 '임시저장' 버튼과 닫을 때의 prompt
     * (handleRequestClose). 후자도 "저장했습니다" 통보가 아니라 저장할지 묻는 것이다.
     */

    /** Address 객체/문자열 배열 → "Name <email>" 또는 raw email 문자열 배열 */
    const addrsToStrings = (arr) => arr.map(v => {
        if (typeof v === "string") return v;
        if (v?.name && v?.email) return `${v.name} <${v.email}>`;
        return v?.email || "";
    }).filter(Boolean);

    const buildPayload = () => {
        // 발송 직전에도 한 번 더 새니타이즈 (백엔드도 새니타이즈 하지만 클라이언트 측 1차 방어)
        //
        // ⚠ DOMPurify v3 기본값은 보수적이라 다음을 자동 제거:
        //   - style 속성 → 표 width / 글자색 / 배경색 등 모든 inline 스타일 사라짐
        //   - data: URI on <img> → 클립보드로 붙여넣은 base64 이미지 통째로 사라짐
        //   - colwidth(Tiptap 비표준), cellpadding/cellspacing/border/align/valign/bgcolor 등 표 속성
        // 이걸 그대로 두면 표·이미지·서식이 발송 직전에 통째로 날아감.
        // 메일 작성기에서 만든 HTML 은 우리가 신뢰하는 출력이므로(이미 Tiptap 이 안전한 노드만 생성),
        // 메일 컨텐츠에 필요한 속성·data:URI 를 명시적으로 허용한다.
        const sanitized = DOMPurify.sanitize(bodyHtml || "", {
            ADD_ATTR: [
                "target", "rel",
                "style", "class",
                "colspan", "rowspan", "colwidth",
                "cellpadding", "cellspacing", "border",
                "width", "height",
                "align", "valign", "bgcolor",
            ],
            ADD_TAGS: ["colgroup", "col"], // 3.x 기본 포함이지만 안전망
            ADD_DATA_URI_TAGS: ["img"],     // <img src="data:..."> 보존 — 백엔드가 cid: 로 변환
        });

        // 발송용 정규화 — DOMPurify 가 통과시킨 HTML 에 검정 보더 / 표 width / colwidth → width 등 inline 주입.
        // 이걸 안 하면 외부 메일 클라이언트(Naver/Gmail 등)에서 표 보더가 invisible 한 채 도착함.
        // 미리보기와 실제 발송이 동일 HTML 을 쓰도록 한 곳에서 처리 (이전엔 미리보기에서만 호출돼 받은 메일과 차이남).
        const safeHtml = normalizeBodyForPreview(sanitized);

        // Tiptap 사용자 본문 + 인용 블록(사용자가 편집했을 수 있는 최신 HTML) 합치기.
        // 인용 블록은 contentEditable 로 사용자 native 편집 가능 — quoteRef 의 현재 innerHTML 추출.
        // schema 변환 없이 외부 메일 표/이미지/style 그대로 보존. 백엔드 materializeInlineImagesForOutgoing
        // 가 인용 블록 안의 inline endpoint URL → 새 cid 변환.
        //
        // safeHtml 이 비어 있으면(= 다시보내기: 위 작성 칸이 없다) 인용만 내보낸다. 여기서 무조건
        // 이어붙이면 본문 맨 위에 "\n" 이 남고, 예전처럼 빈 <p> 가 섞이면 빈 줄이 한 줄 더 생긴다.
        const latestQuote = currentQuoteHtml();
        const finalBodyHtml = latestQuote
                ? (safeHtml ? safeHtml + "\n" + latestQuote : latestQuote)
                : safeHtml;

        // 통합 payload — 세 모드가 같은 모양을 쓴다. 어느 경로로 나갈지는 sendMode 하나로만 표현하고
        // 실제 분기는 서버(MailDispatchService)가 한다. 프론트가 엔드포인트를 고르던 시절엔
        // 모드마다 조립·검증이 갈라져 기능이 새어나갔다(단체엔 첨부 검증 없음, 예약엔 연락처 추천 없음 등).
        const sendMode = bulkMode ? "BULK" : scheduledMode ? "SCHEDULED" : "IMMEDIATE";
        return {
            sendMode,
            // 즉시 발송이면 예약 시각은 무의미 — 남은 값이 딸려가지 않게 명시적으로 null.
            scheduledAt: sendMode === "IMMEDIATE" ? null : (scheduleAt || null),
            id: draftId,
            // 세 모드가 같은 필드를 쓴다. 단체에서 tos 는 '개별 발송 대상 목록' 이 되고,
            // 참조·숨은참조는 일반 메일과 똑같이 개별 발송 한 통마다 붙는다.
            tos:      addrsToStrings(tos),
            ccs:      addrsToStrings(ccs),
            bccs:     addrsToStrings(bccs),
            subject:  subject.trim(),
            bodyHtml: finalBodyHtml,
            // 다시보내기는 Tiptap 이 없어 getText() 로 채워지는 bodyText 가 늘 빈 문자열이다.
            // 그대로 두면 multipart/alternative 의 평문 파트가 비고, 첨부 언급 감지(detectAttachmentKeyword)도
            // 아무것도 못 본다 → 최종 본문에서 직접 평문을 뽑는다. 다른 모드는 기존 동작 그대로.
            bodyText: isResendMode ? htmlToPlainText(finalBodyHtml) : bodyText,
            // attachmentIds 제거됨 — atomic 발송에선 multipart 의 files 부분이 첨부 그 자체.
            // 서버는 attachmentIds 대신 multipart 의 파일 스트림을 받아 그 자리에서 처리.
            inReplyTo,
            references,
            parentMailId,
            // 전달에서 이어받는 원본 첨부 — 새로 올리는 파일(multipart files)과 달리 id 만 보낸다.
            // 사용자가 칩에서 지운 건 여기 안 들어가므로 그대로 빠진다.
            forwardAttachmentIds: attachments.filter(a => a.refId && !a.resend).map(a => a.refId),
            // 다시보내기로 '복사해 올' 원본 첨부 — 목록의 모양은 전달과 같지만 서버가 원본 객체를
            // 가리키는 대신 새 키로 CopyObject 한다. 두 목록을 한 필드로 합치면 그 분기가 사라진다.
            resendSourceMailId,
            resendAttachmentIds: attachments.filter(a => a.refId && a.resend).map(a => a.refId),
        };
    };

    /*
     * saveDraft / ensureDraftId 제거됨 — atomic 발송 패턴 채택.
     * 메일 작성 중 서버 호출 0회. "최종 발송" 시 한 번에 multipart 로 전송.
     * 추후 명시적 "초안 보관" 기능이 필요하면 atomic upload 형태로 별도 추가.
     */

    /**
     * 첨부 추가 — File 객체를 메모리에 보유. 서버 업로드는 "최종 발송" 시점에 atomic 으로 한 번에.
     *
     * <h4>왜 메모리 보유인가</h4>
     * <p>이전엔 첨부마다 즉시 S3 업로드 → 사용자가 발송 안 하고 닫아도 S3 에 orphan 파일.
     * 또 SES 한도가 발송 시점에 발견되어 이미 올라간 첨부를 정리해야 했음.
     * 이젠 모두 브라우저에 보유하고 합계 사전 검증, 발송 시 한 multipart 로 atomic 전송.
     *
     * <h4>합계 사이즈 사전 검증</h4>
     * <p>SES v2 raw 메시지 한도 40MB. base64 인코딩(~33% 팽창) + MIME 헤더 감안 → binary 25MB 안전 한도.
     * 회사 정책상 40MB 이상은 메일이 아닌 별도 대용량 채널 사용. 첨부 추가 시 즉시 합계 체크.
     */
    const ATTACH_SAFE_LIMIT = 25 * 1024 * 1024; // 25MB binary (Gmail 첨부 한도와 동일 · EML ~36MB 로 SES 40MB 여유)

    /* ── 대용량 첨부 ──────────────────────────────────────────────────────────
     *
     * 25MB 한도를 넘겨 EML 에 담을 수 없는 파일은 '거부' 가 아니라 '링크로 전환' 한다.
     * 자동 판정이고 수동 전환 버튼은 두지 않는다 — 자리가 있으면 항상 일반 첨부가 되므로,
     * 대용량은 선택지가 아니라 담을 자리가 없을 때의 fallback 이다.
     *
     * presign 에 mailId 가 필요한데 이 작성창은 발송 전까지 초안을 만들지 않는다(atomic 발송).
     * 그래서 첫 대용량 파일을 고를 때 임시저장을 선행시킨다. 사용자 입장에서도 이득이다 —
     * 2GB 업로드는 수십 분이라, 초안이 없으면 그 사이 탭이 닫히는 순간 전부 날아간다.
     */
    const persistDraftRef = useRef(null);   // 아래에서 정의되는 persistDraft 를 연결(선언 순서 회피)
    const draftIdRef      = useRef(null);
    const ensureDraftPromiseRef = useRef(null);
    /**
     * 이 초안이 '대용량 첨부 때문에 이번 세션에 자동 생성' 된 것인지.
     * 사용자가 임시보관함에서 열어 편집 중인 기존 초안이면 절대 지우면 안 되므로 구분한다.
     */
    const autoCreatedDraftRef = useRef(false);

    /** 초안 id 보장 — 동시에 여러 번 불려도 저장은 한 번만 한다. */
    const ensureDraftForLarge = useCallback(async () => {
        if (draftIdRef.current) return draftIdRef.current;
        if (ensureDraftPromiseRef.current) return ensureDraftPromiseRef.current;
        const p = (async () => {
            const wasNew = !draftIdRef.current;
            const id = await persistDraftRef.current?.();
            if (id) {
                draftIdRef.current = id;
                if (wasNew) autoCreatedDraftRef.current = true;
            }
            return id;
        })().finally(() => { ensureDraftPromiseRef.current = null; });
        ensureDraftPromiseRef.current = p;
        return p;
    }, []);

    const large = useLargeAttachments({ ensureDraft: ensureDraftForLarge, notify });

    // draftId 는 state 라 훅 안의 클로저가 옛 값을 볼 수 있다. ref 로 미러링해 둔다.
    useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

    // 초안 재편집 — 이미 올려둔 대용량을 복원한다.
    // (일반 첨부는 임시저장에 실리지 않아 복원되지 않는다. 대용량만 남는 게 정상이다.)
    useEffect(() => {
        if (!open) return;
        // 초안 재편집이면 서버 목록을 복원하고, 새 메일이면 잔여 용량만 읽는다.
        if (initial?.draftId) large.load(initial.draftId);
        else                  large.refreshQuota();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial?.draftId]);

    /** 로컬 첨부 id 채번 — Date.now() 만 쓰면 같은 밀리초 배치끼리 id 가 겹쳐 삭제가 엉뚱한 걸 지운다. */
    const localAttachSeq = useRef(0);

    /**
     * 표시·전송용 파일명.
     *
     * <p>폴더를 드롭하면 `디자인/시안1.jpg` 처럼 하위 경로가 딸려 온다. 메일 첨부는 평면 목록이라
     * 경로를 버리면 서로 다른 폴더의 같은 이름 파일이 구분되지 않는다. 그렇다고 슬래시를 그대로 두면
     * 서버가 이 이름으로 S3 키를 만들 때({@code S3MailClient.putAttachment}) 가짜 하위 prefix 가
     * 생기므로, 구분자만 밑줄로 바꿔 경로 정보를 이름 안에 남긴다.
     */
    const attachmentDisplayName = (path) =>
        String(path || "attachment").replace(/[\\/]+/g, "_");

    /**
     * 첨부 추가 — {@code File[]} 또는 {@code {file, path}[]} 를 받는다
     * (폴더 드롭은 하위 경로가 필요한데 {@code File.name} 은 읽기 전용이라 별도로 넘긴다).
     *
     * <h4>한도를 넘으면 '전부 거부' 가 아니라 '담을 수 있는 만큼'</h4>
     * <p>폴더를 통째로 끌어다 놓으면 파일이 수십·수백 개다. 그중 한 개가 한도를 넘겼다고 전부
     * 되돌리면 사용자는 어느 것을 빼야 할지도 모른 채 처음부터 다시 해야 한다. 들어온 순서대로
     * 담고 <b>무엇이 몇 개 빠졌는지 명시</b>한다 — 조용히 자르지 않는 것이 핵심이다.
     */
    const handleAttachmentsAdded = async (input) => {
        const picked = (input || [])
            .map(x => (x && x.file ? x : { file: x, path: x?.name }))
            .filter(x => x && x.file);
        if (picked.length === 0) return;

        const currentTotal = attachments.reduce((s, a) => s + (a.sizeBytes || 0), 0);

        // 자리가 있는 만큼 일반 첨부로 담고, 넘치는 것은 대용량(링크)으로 보낸다.
        // 판정은 '순서 우선' — 이미 일반으로 잡힌 건 유지하고 새 파일만 본다. '작은 것부터 채우기' 로
        // 하면 이미 첨부였던 파일이 나중에 링크로 뒤집혀 더 혼란스럽다.
        const accepted = [];
        const overflow = [];
        let running = currentTotal;
        for (const p of picked) {
            if (running + p.file.size > ATTACH_SAFE_LIMIT) { overflow.push(p); continue; }
            running += p.file.size;
            accepted.push(p);
        }

        if (accepted.length > 0) {
            // 메모리에 File 객체 보유 — 발송 직전에 multipart 로 한 번에 업로드
            const newItems = accepted.map((p) => ({
                id: `__local__${localAttachSeq.current++}`,
                filename: attachmentDisplayName(p.path || p.file.name),
                sizeBytes: p.file.size,
                mimeType: p.file.type || "application/octet-stream",
                file: p.file,          // ← File 객체 그대로 보관 (FormData 에 직접 append)
                local: true,           // 화면 표시용 마커
            }));
            setAttachments(prev => [...prev, ...newItems]);
        }

        if (overflow.length > 0) {
            // 단체 발송은 대용량을 아직 지원하지 않는다(수신자별 링크 발급 설계가 따로 필요).
            // 그쪽에서는 예전처럼 거부하되, 왜 안 되는지는 분명히 말한다.
            if (bulkMode) {
                const names = overflow.slice(0, 3).map(p => attachmentDisplayName(p.path || p.file.name));
                const more  = overflow.length > 3 ? ` 외 ${overflow.length - 3}개` : "";
                notify(
                    `단체 발송은 첨부 합계 25MB 까지만 가능합니다. 제외: ${names.join(", ")}${more}.`,
                    "error"
                );
            } else {
                large.add(overflow);
                const names = overflow.slice(0, 2).map(p => attachmentDisplayName(p.path || p.file.name));
                const more  = overflow.length > 2 ? ` 외 ${overflow.length - 2}개` : "";
                notify(
                    `${names.join(", ")}${more} 은(는) 첨부 용량(25MB)을 넘어 대용량 첨부로 보냅니다. ` +
                    `본문에 다운로드 링크가 들어갑니다.`,
                    "info"
                );
            }
        }
    };

    /**
     * 첨부 제거 — 모든 첨부가 메모리에 있으므로 그냥 state 에서 빼면 끝.
     * 이전엔 deleteAttachment API 호출 → S3·DB 삭제했으나 이젠 발송 전엔 서버에 없음.
     */
    const handleAttachmentRemove = (attId) => {
        setAttachments(prev => prev.filter(a => a.id !== attId));
    };

    /* ── 드래그&드롭 첨부 ──────────────────────────────────────────────────────
     *
     * 작성 영역 어디에 떨어뜨려도 첨부로 잡는다. 파일 추가는 반드시
     * handleAttachmentsAdded 를 거친다 — 25MB 합계 검증이 거기 한 곳에만 있어서,
     * 드롭이 별도 경로를 쓰면 그 검사를 통째로 우회하게 된다.
     */
    const [dragActive, setDragActive] = useState(false);
    /**
     * dragenter/dragleave 는 자식 요소를 지날 때마다 쌍으로 발생한다.
     * 깊이를 세지 않으면 마우스가 입력칸·에디터 경계를 지날 때마다 오버레이가 깜빡인다.
     */
    const dragDepth = useRef(0);

    /** 드래그 중인 것이 파일인가 — 텍스트·이미지 선택을 끌 때는 오버레이를 띄우지 않는다. */
    const isFileDrag = (e) =>
        Array.from(e.dataTransfer?.types || []).includes("Files");

    /**
     * 첨부에서 뺄 OS 부산물.
     *
     * <p>폴더를 통째로 끌어다 놓으면 사용자가 만든 적 없는 파일들이 딸려 온다. 받는 사람 입장에선
     * 정체불명 첨부이고, 25MB 한도도 갉아먹는다. 점으로 시작하는 파일 <b>전부</b>를 빼지는 않는다 —
     * {@code .env} 처럼 일부러 보내는 경우가 있다.
     */
    const IGNORED_FILENAMES = new Set([".DS_Store", "Thumbs.db", "desktop.ini"]);
    const isIgnoredFile = (name) =>
        IGNORED_FILENAMES.has(name) || name.startsWith("._"); // ._* = macOS 리소스 포크

    /**
     * 드롭된 항목에서 파일을 모은다 — 폴더면 하위까지 재귀.
     *
     * @param entries 드롭 핸들러가 <b>동기적으로</b> 미리 뽑아 둔 FileSystemEntry 목록
     * @return {@code [{file, path}]} — path 는 드롭한 폴더를 기준으로 한 상대경로
     */
    const collectFromEntries = async (entries) => {
        const out = [];

        const walk = async (entry, prefix) => {
            if (entry.isFile) {
                const file = await new Promise((res, rej) => entry.file(res, rej));
                if (isIgnoredFile(file.name)) return;
                out.push({ file, path: prefix + file.name });
                return;
            }
            if (!entry.isDirectory) return;
            const reader = entry.createReader();
            // readEntries 는 한 번에 최대 100개만 준다 — 빈 배열이 올 때까지 반복해야
            // 파일이 많은 폴더의 뒷부분이 조용히 누락되지 않는다.
            for (;;) {
                const batch = await new Promise((res, rej) => reader.readEntries(res, rej));
                if (!batch || batch.length === 0) break;
                for (const child of batch) await walk(child, prefix + entry.name + "/");
            }
        };

        for (const entry of entries) {
            try {
                await walk(entry, "");
            } catch (err) {
                console.error("[compose] 드롭 항목 읽기 실패", entry?.name, err);
                notify(`'${entry?.name || "항목"}' 을(를) 읽지 못했습니다`, "warning");
            }
        }
        return out;
    };

    const handleDragEnter = (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragActive(true);
    };

    const handleDragOver = (e) => {
        if (!isFileDrag(e)) return;
        // preventDefault 가 없으면 브라우저가 파일을 '열어서' 페이지를 통째로 갈아치운다.
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    const handleDragLeave = (e) => {
        if (!isFileDrag(e)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragActive(false);
    };

    /**
     * 드롭 — 본문(Tiptap) 위에 떨어뜨려도 <b>첨부가 이긴다</b>.
     *
     * <p>에디터 기본 동작은 이미지를 본문에 인라인으로 박는 것인데, 인라인 이미지는
     * 첨부 합계 검사(25MB)에도 서버의 첨부 검사에도 안 잡혀 발송 직전에야 SES 40MB 한도에
     * 걸린다(= 보낸 뒤 실패). 인라인이 필요하면 붙여넣기로 하면 되므로, 드롭은 첨부로 고정한다.
     */
    const handleDrop = async (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current = 0;
        setDragActive(false);

        // ⚠ DataTransferItemList 는 이 핸들러가 끝나면 무효화된다. entry 를 <b>먼저 동기적으로</b>
        //    전부 뽑아 두고 나서 비동기 순회를 시작해야 한다 — 순서를 뒤집으면 "가끔 아무것도
        //    안 붙는" 형태로 나타나고, 재현이 잘 안 돼 찾기 어렵다.
        const entries = [];
        const items = e.dataTransfer.items;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                const entry = items[i].webkitGetAsEntry?.();
                if (entry) entries.push(entry);
            }
        }
        // webkitGetAsEntry 를 못 쓰는 환경 폴백 — 폴더는 못 읽고 파일만 들어온다.
        const fallback = Array.from(e.dataTransfer.files || []).map(f => ({ file: f, path: f.name }));

        const collected = entries.length > 0 ? await collectFromEntries(entries) : fallback;
        if (collected.length === 0) {
            if (entries.length > 0) notify("폴더 안에 첨부할 파일이 없습니다", "warning");
            return;
        }
        await handleAttachmentsAdded(collected);
    };

    /**
     * 발송 전 "전송 클릭 시점" 전체 메일 크기 사전 검증 — 초과 메시지를 반환(정상이면 null).
     *
     * <h4>왜 발송 시점에 또 검사하나 (첨부 추가 때 handleAttachmentsAdded 가 이미 25MB 를 막는데)</h4>
     * <ol>
     *   <li><b>인라인 이미지 사각지대</b>: 첨부 추가(dropzone) 경로만 25MB 로 막혀 있고, 본문에
     *       붙여넣은 인라인 이미지(bodyHtml 안 data:base64)는 클라이언트·서버 어디서도 크기 검사가
     *       안 된다. 서버(send-atomic)의 25MB 검사도 <b>첨부 파일 합계</b>만 보고 본문/인라인은 안 본다.
     *       → 큰 인라인 이미지가 많으면 첨부 합계는 작아도 실제 EML 이 SES 40MB 한도를 넘겨
     *       <b>"보낸 뒤"</b> SES 가 거부한다(발송 후 실패 = 사용자 유실 위험).</li>
     *   <li><b>노출 시점</b>: 지금은 서버 4xx 를 받은 "후"에야 토스트가 뜬다. 사용자가 원하는 건
     *       "전송 버튼 누를 때" 즉시 차단이다.</li>
     * </ol>
     *
     * <h4>동작</h4>
     * <p>전송 클릭 시 첨부+본문(인라인 포함)을 합쳐 EML 예상치를 계산해 한도 초과면 네트워크 호출
     * 전에 문자열(메시지)을 반환한다. 호출부는 notify 후 즉시 return → 작성 내용·모달 유지(유실 없음).
     *
     * <h4>임계</h4>
     * <ul>
     *   <li>첨부 합계 &gt; 25MB → 서버와 동일 기준(확정 초과, 첨부 안내 메시지)</li>
     *   <li>EML 예상치 &gt; 38MB → 본문/인라인 포함 총량이 SES 40MB raw 한도 아래 안전 마진.
     *       base64 팽창(4/3) 반영. 인라인 이미지는 bodyHtml 안 data:base64 라 문자열 바이트로 잡힌다.
     *       첨부만 25MB(≈36MB EML)인 정상 메일은 통과하고, 인라인 폭증 케이스만 차단한다.</li>
     * </ul>
     */
    const EML_SAFE_LIMIT = 38 * 1024 * 1024; // EML 안전 한도 — SES 40MB raw(≈40,000,000B) 대비 마진 (서버 MailSendService 와 동일)
    const checkOutgoingSize = (payload) => {
        // ★ 대용량 첨부가 아직 올라가는 중이면 발송을 막는다.
        //   서버(activateForSend)도 PENDING 이 있으면 거부하지만, 여기서 먼저 막아야
        //   "보내기를 눌렀는데 에러" 대신 "왜 못 보내는지" 를 알려줄 수 있다.
        //   막지 않으면 사용자가 붙였다고 믿는 파일이 조용히 빠진 채 나간다 —
        //   2GB 업로드는 수십 분이라 "다 됐겠지" 하고 누르는 상황이 실제로 자주 나온다.
        if (large.hasBlocking) {
            return `대용량 첨부 ${large.busyCount}개가 아직 업로드 중입니다. 완료된 뒤 발송해 주세요.`;
        }
        if (large.errorCount > 0) {
            return `대용량 첨부 ${large.errorCount}개가 업로드에 실패했습니다. ` +
                   `다시 시도하거나 목록에서 제거한 뒤 발송해 주세요.`;
        }

        // 아래 두 검사는 이제 안전망이다 — 25MB 를 넘는 파일은 handleAttachmentsAdded 가
        // 대용량으로 보내므로 attachments 합계가 한도를 넘을 일이 없다.
        const attachBytes = attachments.reduce((s, a) => s + (a.sizeBytes || 0), 0);
        if (attachBytes > ATTACH_SAFE_LIMIT) {
            return `첨부 합계가 25MB 를 초과합니다 (현재 ${(attachBytes / 1024 / 1024).toFixed(1)}MB). ` +
                   `메일 한도(40MB) 초과로 발송할 수 없습니다. 일부 파일을 제거하거나 별도 공유 채널을 이용하세요.`;
        }
        // 본문(인라인 base64 이미지 포함) UTF-8 바이트 + 첨부 base64 팽창(4/3) → EML 근사치
        const htmlBytes = new TextEncoder().encode((payload && payload.bodyHtml) || bodyHtml || "").length;
        const estEml = Math.ceil(attachBytes / 3) * 4 + htmlBytes + 128 * 1024; // +128KB 헤더/경계/plain 여유
        if (estEml > EML_SAFE_LIMIT) {
            return `메일 전체 크기가 너무 큽니다 (본문·인라인 이미지 포함 약 ${(estEml / 1024 / 1024).toFixed(1)}MB). ` +
                   `메일 한도(40MB) 초과로 발송할 수 없습니다. 인라인 이미지를 줄이거나 파일 첨부·링크로 대체하세요.`;
        }
        return null;
    };

    /**
     * 받는이 chip 들 중 "직원" 도 "내 연락처" 도 아닌 외부 자유 입력 주소만 추출.
     *  - 객체이고 _group === "직원" / "내 연락처" 면 제외
     *  - 문자열 (raw email) 또는 _group 없는 객체는 외부로 간주
     *  - "Name <email>" 형태 파싱
     */
    /** 우리 회사 도메인이면 자동 등록 prompt 에서 제외 — 사내 직원에게 연락처 prompt 안 뜨도록. */
    const isInternalDomain = (email) => {
        const at = email.indexOf("@");
        if (at < 0) return false;
        const dom = email.slice(at + 1).toLowerCase();
        for (const d of internalDomains) {
            if (!d) continue;
            if (dom === d || dom.endsWith("." + d)) return true;
        }
        return false;
    };

    /**
     * 발송한 주소 중 "직원도 내 연락처도 아닌" 외부 주소만 추려 반환 — 발송 후 연락처 등록 추천용.
     *
     * <p>세 모드가 같은 받는이 state 를 쓰므로 여기도 분기가 없다. 예전엔 이 함수가 즉시 발송
     * 경로에서만 호출돼 <b>예약·단체로 보낸 새 거래처는 아무리 보내도 연락처 등록 추천이 뜨지 않았다</b>.
     */
    const collectUnknownExternals = () => {
        const all = [...tos, ...ccs, ...bccs];
        const out = [];
        const seen = new Set();
        for (const v of all) {
            let email, name = "";
            if (typeof v === "string") {
                const m = v.match(/^\s*"?([^"<>]*)"?\s*<([^>]+)>\s*$/);
                if (m) { name = m[1].trim(); email = m[2].trim(); }
                else   { email = v.trim(); }
            } else if (v && typeof v === "object") {
                if (v._group === "직원" || v._group === "내 연락처") continue;
                email = (v.email || "").trim();
                name  = v.name  || "";
            }
            if (!email) continue;
            // 사내 도메인은 직원으로 가정 — 외부 거래처/협력사가 아니므로 자동 prompt 에서 제외
            if (isInternalDomain(email)) continue;
            const key = email.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ name, email });
        }
        return out;
    };

    /**
     * "보내기" 클릭 — 미리보기 다이얼로그 즉시 open. AI 첨부 감지는 백그라운드.
     *
     * <h4>이전 구현의 UX 문제</h4>
     * <p>이전엔 AI 호출(1~3초)을 await 한 후 미리보기를 열었음 → "보내기" 누른 뒤 1~3초 동안
     * 화면 무반응 → 사용자가 다시 클릭하거나 답답함을 느낌.
     *
     * <h4>현재 구현 — 비동기 패턴</h4>
     * <ol>
     *   <li>검증 + payload 빌드 + 미리보기 즉시 open (사용자가 본문 마지막 확인)</li>
     *   <li>AI 감지는 background fire-and-forget</li>
     *   <li>AI 결과 도착 시 setAiAttachWarn 으로 미리보기 안 경고 배너 업데이트</li>
     *   <li>사용자가 AI 결과 도착 전에 "최종 발송" 클릭 시 그냥 발송 — 경고는 next time</li>
     * </ol>
     */
    const handleSend = async () => {
        const payload = await validateForDispatch();
        if (!payload) return;

        // 미리보기 즉시 open — 사용자에게 즉각 시각 피드백
        setAiAttachWarn(detectAttachmentKeyword(payload.subject, payload.bodyText, attachments));
        setPreviewPayload(payload);
        setPreviewLargeBlock("");
        setPreviewOpen(true);

        // 대용량 첨부 블록은 '발송 시점' 에 서버가 본문에 주입한다. 미리보기에서 그냥 두면
        // 수신자가 볼 링크가 통째로 빠진 화면을 보여주게 되어, 이 모달의 약속
        // ("받는 이에게 이렇게 보내집니다")이 거짓이 된다. 그래서 발송 경로와 같은 렌더러가
        // 만든 블록을 받아 붙인다. 모달은 먼저 띄우고 도착하면 갱신한다 — 네트워크를 기다리느라
        // 클릭 반응이 늦어지면 사용자는 버튼이 안 먹은 줄 안다.
        if (large.items.length > 0 && draftIdRef.current) {
            try {
                const res = await mailApi.largePreviewBlock(
                    draftIdRef.current, payload.sendMode === "SCHEDULED" ? scheduleAt : undefined);
                if (res?.html) setPreviewLargeBlock(res.html);
            } catch { /* 블록을 못 받아도 미리보기 자체는 보여준다 */ }
        }
    };

    /**
     * 발송 공통 검증 — 세 모드가 <b>같은 검증</b>을 지나게 하는 단일 지점.
     * <p>예전엔 즉시/예약/단체가 각자 다른 순서로 다른 항목을 검사해서, 단체만 첨부 크기 검증이
     * 빠지거나 예약만 빈 메일 확인이 없는 식으로 어긋났다.
     * @return 계속 진행해도 되면 payload, 중단해야 하면 null (안내는 이 안에서 이미 띄운다)
     */
    const validateForDispatch = async () => {
        const payload = buildPayload();
        if (payload.tos.length === 0) {
            notify(bulkMode ? "받는이(이메일 목록)를 입력해주세요" : "받는이를 입력해주세요", "warning");
            return null;
        }
        // 예약 시각 — 예약 모드는 필수, 단체 모드는 선택(비우면 즉시 발송).
        // 값이 있으면 어느 모드든 미래여야 한다(서버도 같은 규칙으로 다시 막는다).
        if (scheduledMode && !scheduleAt) { notify("예약 시간을 선택해주세요", "warning"); return null; }
        if (scheduleAt && new Date(scheduleAt).getTime() <= Date.now() + 60 * 1000) {
            notify("예약 시간은 현재보다 1분 이상 이후여야 합니다", "warning");
            return null;
        }
        // 크기 사전 검증 — 네트워크를 태우기 전에 막아 작성 내용을 잃지 않게 한다.
        const sizeErr = checkOutgoingSize(payload);
        if (sizeErr) { notify(sizeErr, "error"); return null; }

        const plain = (payload.bodyText || "").trim() || (bodyHtml || "").replace(/<[^>]*>/g, "").trim();
        if (!payload.subject && !plain) {
            const ok = await confirm({
                title: "빈 메일 발송",
                message: "제목과 본문이 비어있습니다. 정말 보낼까요?",
                confirmLabel: "보내기",
            });
            if (!ok) return null;
        }
        return payload;
    };

    /**
     * 실제 발송 — 세 모드가 <b>같은 엔드포인트·같은 후처리</b>를 탄다.
     *
     * <p>모드별로 다른 건 서버가 돌려주는 결과 문구뿐이다. 특히 "새 연락처 등록 추천" 은 예전에
     * 즉시 발송 경로에만 있었는데, 예약·단체로 보낸 새 거래처야말로 주소록에 남겨야 할 대상이라
     * 이제 세 모드 공통으로 돌린다.
     */
    const performDispatch = async (payload) => {
        if (sendingRef.current) return;
        sendingRef.current = true;
        setSending(true);
        // ★ 연락처 후보를 '보내기 전에' 확정해 둔다.
        //   발송이 끝나면 모달이 닫히고 받는이 state 도 정리되는데, 그 뒤에 수집하면 무엇을 추천할지가
        //   state 초기화 타이밍에 의존하게 된다. 값으로 먼저 붙잡아 두면 순서와 무관해진다.
        const externals = collectUnknownExternals();
        try {
            const filesToUpload = attachments.filter(a => a.file instanceof File).map(a => a.file);

            // 진행 표시 — 브라우저가 '정확히 아는' 구간만 %로 보여준다.
            //   업로드 구간: multipart 전송 진행률(onUploadProgress) — 실제 값이다.
            //   그 이후:     서버가 S3 저장 → EML 조립 → SES 발송을 단일 동기 요청 안에서 처리하므로
            //                브라우저에서 관측할 방법이 없다. 가짜 %를 만들지 않고 '보내는 중' 으로 둔다.
            setSendPhase(filesToUpload.length > 0 ? { label: "첨부 올리는 중", pct: 0 } : { label: "보내는 중", pct: null });
            const res = await mailApi.dispatch(payload, filesToUpload, (pct) => {
                setSendPhase(pct >= 100
                    ? { label: "보내는 중", pct: null }   // 업로드 완료 = 이제 서버 처리 구간
                    : { label: "첨부 올리는 중", pct });
            });
            const result = res?.data ?? res ?? {};
            const n = result.recipientCount ?? payload.tos.length;

            notify(
                payload.sendMode === "BULK"
                    ? (payload.scheduledAt
                        ? `${n}명 단체 예약 발송을 등록했습니다. 예약함 › 단체 발송에서 확인하세요.`
                        : `${n}명에게 개별 발송을 등록했습니다. 예약함 › 단체 발송에서 진행을 확인하세요.`)
                    : payload.sendMode === "SCHEDULED"
                        ? "메일을 예약했습니다. 예약함에서 확인·취소할 수 있습니다."
                        : "메일을 발송했습니다",
                "success");

            // 발송 성공 — 작성 상태 정리. 미리보기를 거쳐 온 경우도 여기서 함께 닫는다.
            // 자동저장 가드를 여기서 세운다. 안 세우면 발송 직후 모달이 닫히면서 이탈 저장이 걸려
            // 방금 보낸 메일이 초안으로 한 벌 더 남는다(받는이·본문이 그대로라 사용자 눈엔 '중복 발송'
            // 직전 상태로 보인다). ref 라서 리렌더를 기다리지 않고 이 자리에서 곧바로 적용된다.
            skipAutoSaveRef.current = true;
            setDraftId(null);
            draftIdRef.current = null;
            autoCreatedDraftRef.current = false;
            // 대용량 첨부는 발송된 메일에 그대로 붙어 있다(만료 시계가 시작됐다).
            // 작성창 목록만 비운다 — 여기서 삭제하면 방금 보낸 링크가 죽는다.
            large.reset();
            setPreviewOpen(false);
            setPreviewPayload(null);
            setPreviewLargeBlock("");
            setAiAttachWarn(null);
            if (payload.sendMode !== "IMMEDIATE") setScheduleAt("");

            // 외부 주소가 있으면 새 연락처 등록 추천 — 이미 내 주소록에 있는 건 제외.
            await runContactRegisterPrompt(externals);
        } catch (e) {
            // 서버는 MAIL_SEND_FAILED 응답에 phase·rolledBack 을 실어 준다. 다만 util/http.js 인터셉터가
            // axios error 를 Error(message) 로 바꿔 던지므로 여기 도달하는 건 message 문자열이다.
            // 그 message 자체가 "무엇이 실패했고 / 메일은 안 나갔고 / 내용은 남아있다" 를 모두 담고 있다
            // (MailSendFailedException 참조). 그래서 그대로 노출하는 것이 가장 정확하다.
            notify(e?.message
                || "발송에 실패했습니다. 네트워크 상태를 확인해주세요.", "error");
        } finally {
            setSending(false);
            setSendPhase(null);
            sendingRef.current = false;
        }
    };

    /**
     * 발송 후 "새 연락처 등록" 추천 — 백엔드에 기존 이메일을 물어 이미 있는 건 걸러낸다.
     * <p>추천할 게 없으면 그대로 onSent 로 넘겨 모달을 닫는다.
     */
    const runContactRegisterPrompt = async (externals) => {
        if (!externals || externals.length === 0) { onSent?.(); return; }
        let existing = new Set();
        try {
            const res = await mailApi.contactsExisting(externals.map(e => e.email));
            const data = res?.data ?? res ?? [];
            // 백엔드는 Set<String> JSON 직렬화 — 보통 배열로 온다.
            existing = new Set((Array.isArray(data) ? data : [...data]).map(s => String(s).toLowerCase()));
        } catch { /* 실패해도 추천 자체는 계속 — 사용자가 "기존 연락처임" 직접 인지 */ }
        const fresh = externals.filter(e => !existing.has(e.email.toLowerCase()));
        if (fresh.length > 0) setRegisterPrompt(fresh);
        else onSent?.();
    };

    /**
     * 예약·단체 등록 — 미리보기를 거치지 않고 바로 보낸다.
     *
     * <p>둘을 한 함수로 두는 이유: 검증도 요청도 후처리도 완전히 같고, 예약이냐 단체냐는 payload 의
     * {@code sendMode} 한 필드로만 갈린다(그 분기는 서버가 한다). 예전엔 이게 별도 함수 두 개였고
     * 그래서 한쪽에만 검증이 빠지는 식으로 계속 어긋났다.
     *
     * <p>즉시 발송({@code handleSend})만 미리보기 단계를 한 번 더 거치고, 그 뒤는 여기와 같은 경로다.
     */
    const handleQuickDispatch = async () => {
        const payload = await validateForDispatch();
        if (payload) await performDispatch(payload);
    };

    /**
     * 클라이언트 첨부 언급 감지 — 정규식 키워드 매칭만. 서버 / OpenAI 호출 없음.
     *
     * <h4>왜 클라이언트인가</h4>
     * <p>이전엔 OpenAI 로 의미 분석했지만 1~3초 + 비용 + race condition. 트리거 단어가 본문에
     * 있고 첨부가 0개면 사용자에게 미리보기 배너로 알리는 정도면 충분 — false positive 가 좀
     * 있어도 "첨부 빠뜨림 사고 방지" 라는 목표엔 도달.
     *
     * <h4>로직</h4>
     * <ul>
     *   <li>첨부가 이미 있으면 검사 의미 없음 → null</li>
     *   <li>본문이 너무 짧으면 (10자 미만) 의미 없음 → null</li>
     *   <li>한국어/영어 첨부 트리거 단어 검사 — 대소문자 무시, 단순 substring</li>
     *   <li>매칭되면 evidence 로 그 단어가 포함된 짧은 컨텍스트 추출</li>
     * </ul>
     */
    const ATTACH_KEYWORDS = [
        "첨부", "별첨", "동봉", "송부", "보내드립", "보내드리",
        "전달드립", "파일", "이미지", "사진",
        "attached", "attach", "enclosed", "see attached", "please find", "find attached",
    ];
    const detectAttachmentKeyword = (subject, bodyText, atts) => {
        if ((atts?.length ?? 0) > 0) return null;
        const trimmed = (bodyText || "").trim();
        if (trimmed.length < 10) return null;
        const hay = ((subject || "") + "\n" + trimmed).toLowerCase();
        const evidence = [];
        for (const kw of ATTACH_KEYWORDS) {
            const idx = hay.indexOf(kw.toLowerCase());
            if (idx < 0) continue;
            // 키워드 주변 ±20자 컨텍스트 추출
            const start = Math.max(0, idx - 20);
            const end   = Math.min(hay.length, idx + kw.length + 20);
            evidence.push(hay.slice(start, end).replace(/\s+/g, " ").trim());
            if (evidence.length >= 3) break;
        }
        if (evidence.length === 0) return null;
        return { mentionsAttachment: true, evidence };
    };

    /**
     * 미리보기 다이얼로그의 "최종 발송" — 실제 SES 발송.
     *
     * <h4>이전 버그</h4>
     * <p>예전엔 draftId 가 있으면 {@code mailApi.send(draftId)} 만 호출했는데, 이 API 는
     * <b>DB 의 draft 행</b> 을 읽어 발송함 — UI 의 최신 입력이 아니라.
     *
     * <pre>
     *   사용자가 메일 작성 시작
     *      ↓ (10초 idle)
     *   auto-save 가 비어 있는 수신자로 1차 저장 → draftId 생김
     *      ↓
     *   사용자가 받는이/제목/AI 검토 거쳐 보내기 클릭
     *      ↓
     *   handleFinalSend 가 mailApi.send(draftId) 호출
     *      ↓
     *   백엔드는 DB 에 저장된 빈 수신자로 발송 시도 → "받는이가 비어있습니다" 에러
     *      ↓
     *   다음 auto-save 가 fired 된 후 재시도하면 통과 — 사용자가 "한 번 더 누르면 됨" 으로 체감
     * </pre>
     *
     * <h4>해결</h4>
     * <p>항상 {@code composeAndSend(payload)} 사용. payload.id 에 draftId 가 들어있으면 백엔드가
     * UPDATE+SEND, 없으면 INSERT+SEND. 어느 경우든 <b>UI 최신 상태</b>로 발송.
     */
    const handleFinalSend = async () => {
        if (!previewPayload) return;
        // 방어선 — 미리보기 사이에 본문/첨부가 바뀌었을 수 있으므로 실제 네트워크 발송 직전에도 크기 재검증.
        const sizeErr = checkOutgoingSize(previewPayload);
        if (sizeErr) { notify(sizeErr, "error"); return; }
        // 실제 발송·후처리는 예약/단체와 완전히 같은 경로(performDispatch)를 탄다.
        // 즉시 발송만 미리보기 단계를 한 번 더 거칠 뿐, 그 뒤는 세 모드가 동일하다.
        await performDispatch(previewPayload);
    };

    /** prompt 닫기 — onSent 위임 (모달 자체도 닫힘) */
    const closeRegisterPrompt = () => {
        setRegisterPrompt(null);
        onSent?.();
    };

    /**
     * 임시저장 — 제목·받는이·본문만 서버 초안(DRAFT)으로 남긴다.
     *
     * <h4>첨부는 저장하지 않는다</h4>
     * <p>보내지도 않을 파일이 S3 에 쌓이고, 그 초안을 지우기 전까지 계속 남는다. 임시보관함에
     * 몇 년 치가 누적된 전례가 있어(운영 525건·259MB) 그 무게를 지지 않기로 했다.
     * 대신 <b>조용히 버리지 않고</b> 저장 전에 확인을 받는다 — 발송 시점에야 첨부가 없어진 걸
     * 알아채면 그게 진짜 사고다.
     *
     * <h4>버튼과 닫기 확인이 같은 함수를 쓴다</h4>
     * <p>주기 자동저장(10초)은 race condition·초안 누적·서버 부하 때문에 걷어냈고 되살리지 않는다.
     * 대신 서버로 나가는 순간은 두 가지다 — <b>사용자가 버튼을 누를 때</b>와
     * <b>닫을 때 뜬 확인에서 '임시저장' 을 고를 때</b>(handleRequestClose). 둘 다 사용자가
     * 그 자리에서 고른 결과라 세션당 호출 횟수가 유한하다.
     * 실제 저장은 {@link persistDraft} 한 곳에서만 일어난다.
     */
    const persistDraft = async () => {
        const payload = buildPayload();
        setSavingDraft(true);
        try {
            const res = await mailApi.saveDraft({
                id: draftId,
                tos: payload.tos,
                ccs: payload.ccs,
                bccs: payload.bccs,
                subject: payload.subject,
                bodyHtml: payload.bodyHtml,
                bodyText: payload.bodyText,
                inReplyTo: payload.inReplyTo,
                references: payload.references,
                parentMailId: payload.parentMailId,
                // forwardAttachmentIds / resendAttachmentIds 는 일부러 뺀다 — 첨부를 담지 않는 저장이다.
                // (handleSaveDraft 가 "첨부 N개는 포함되지 않습니다" 로 미리 확인을 받는다)
            });
            const savedId = res?.data ?? res;
            if (savedId) {
                setDraftId(savedId);            // 다음 저장이 같은 행을 갱신하도록
                draftIdRef.current = savedId;   // 대용량 presign 이 즉시 볼 수 있게 동기 반영
            }
            return savedId;
        } finally {
            setSavingDraft(false);
        }
    };
    // 대용량 첨부가 초안을 선행 생성할 때 쓴다(선언 순서 때문에 ref 로 연결).
    persistDraftRef.current = persistDraft;

    /**
     * 임시저장 직후 대용량 첨부 정리.
     *
     * <p>임시보관함은 넣어두고 잊는 곳이다. 일반 첨부를 초안에 안 싣기로 한 것도 같은 이유인데
     * (운영에서 수백 건이 몇 년치 쌓인 전례), 대용량은 건당 최대 2GB 라 규모가 다르다 —
     * 100명이 각자 하나씩만 잊어도 100GB 가 S3 에 남고 그건 매달 청구된다.
     *
     * <p>사용자가 올린 시간을 버리는 동작이므로 <b>확인창에서 반드시 먼저 알린 뒤에만</b> 부른다.
     */
    const purgeLargeAfterDraftSave = async (mailId) => {
        if (!mailId || large.items.length === 0) return;
        try { await mailApi.largeDeleteUnsent(mailId); } catch { /* 스윕이 회수한다 */ }
        large.reset();
    };

    const handleSaveDraft = async () => {
        if (savingDraft) return;

        // 대용량은 이미 서버에 올라가 있어 '지운다' 는 사실을 반드시 먼저 알린다 —
        // 모르고 눌렀다가 수십 분짜리 업로드가 사라지면 그게 더 큰 사고다.
        if (attachments.length > 0 || large.items.length > 0) {
            const ok = await confirm({
                title: "임시저장",
                message: [
                    attachments.length
                        && `일반 첨부 ${attachments.length}개는 임시저장에 포함되지 않습니다.`,
                    large.items.length
                        && `업로드한 대용량 첨부 ${large.items.length}개(${formatBytes(large.totalBytes)})는 `
                           + `임시저장 시 삭제됩니다.`,
                    "",
                    "제목·받는이·본문만 저장되며, 발송할 때 첨부를 다시 넣어야 합니다.",
                    "",
                    "이대로 저장할까요?",
                ].filter(v => v !== false && v != null).join("\n"),
                confirmLabel: "저장",
            });
            if (!ok) return;
        }

        try {
            const savedId = await persistDraft();
            await purgeLargeAfterDraftSave(savedId ?? draftIdRef.current);
            notify("임시보관함에 저장되었습니다", "success");
        } catch (e) {
            notify(e?.message || "임시저장에 실패했습니다", "error");
        }
    };

    /**
     * 작성 중인 메일 폐기 — atomic 패턴이라 서버에 저장된 게 없음. 메모리만 정리하고 닫기.
     * (이전엔 서버 saveDraft 가 fire 됐을 수 있어 deleteDraft API 호출했음)
     *
     * <p>단, 임시저장을 눌러 <b>서버에 초안이 생긴 뒤</b>라면 그 행은 남는다. 여기서 지우지 않는 이유는
     * '폐기'가 지우는 대상이 "작성 중인 내용"이지 "이미 임시보관함에 넣어 둔 메일"이 아니기 때문이다.
     * 초안 삭제는 임시보관함에서 한다(그쪽은 휴지통으로 옮긴다 — 메일 영구 보존 정책).
     *
     * <p>여기서 {@code skipAutoSaveRef} 를 세우는 것이 중요하다. 세우지 않으면 폐기를 눌러 닫는
     * 순간 닫기 확인이 한 번 더 떠 <b>방금 버리기로 한 내용을 두고 저장할지 또 묻는다.</b>
     */
    const handleDiscardDraft = async () => {
        const ok = await confirm({
            title: "작성 폐기",
            message: "작성 중인 메일을 폐기하고 닫을까요?\n임시보관함에도 저장되지 않습니다.",
            confirmLabel: "폐기",
            variant: "danger",
        });
        if (!ok) return;
        skipAutoSaveRef.current = true;

        // 대용량 첨부 때문에 초안이 이미 만들어졌을 수 있다. 그대로 두면 위 안내
        // ("임시보관함에도 저장되지 않습니다")가 거짓이 되고, 사용자가 폐기했다고 믿는 메일이
        // 임시보관함에 남는다. 진행 중 업로드를 끊고 초안도 함께 정리한다.
        // (대용량 파일 자체는 발송된 적이 없어 7일 뒤 스윕이 회수한다.)
        // 대용량 첨부 때문에 '자동으로' 만들어진 초안만 지운다.
        // 임시보관함에서 열어 편집 중이던 기존 초안은 '폐기'(=작성 중 내용 버리기)의 대상이 아니다 —
        // 그건 임시보관함에서 지우는 행위이고 여기서 대신 결정하면 안 된다.
        const discardedDraftId = autoCreatedDraftRef.current ? draftIdRef.current : null;
        large.reset();
        setDraftId(null);
        draftIdRef.current = null;
        autoCreatedDraftRef.current = false;
        if (discardedDraftId) {
            try { await mailApi.largeDeleteUnsent(discardedDraftId); } catch { /* 스윕이 회수 */ }
            try { await mailApi.deleteDraft(discardedDraftId); } catch { /* 실패해도 닫는다 */ }
        }
        onClose();
    };

    /**
     * 저장할 만한 내용이 있는지 — 닫을 때 저장 여부를 <b>물어볼지</b>의 유일한 방아쇠.
     *
     * <h4>⚠ 서명과 인용을 반드시 걷어내야 한다</h4>
     * <p>기본 서명은 작성기가 열리면 자동으로 삽입되고(insertInitialSignature), 답장·전달은
     * 인용문이 미리 들어가 있다. 그래서 <b>본문은 아무것도 안 써도 절대 비어 있지 않다.</b>
     * 이 둘을 빼지 않고 "본문이 있으면 물어본다" 로 판정하면 <b>작성창을 열었다 닫기만 해도
     * 확인 창이 뜬다</b> — 아무것도 안 썼는데 매번 답을 요구하는 창만큼 빨리 무시하게 되는 것도 없다.
     * (저장을 눌러 버릇하면 임시보관함이 그만큼 쌓인다. 이미 이관분 525건이 들어 있다.)
     *
     * <p>DOMParser 로 파싱하는 이유는 문자열 치환이 중첩 태그를 못 다루기 때문이고,
     * DOMParser 문서는 비활성(inert)이라 이미지 로딩·스크립트 실행이 일어나지 않는다.
     */
    const hasUserContent = () => {
        if (tos.length || ccs.length || bccs.length) return true;
        if (subject.trim()) return true;
        if (attachments.length) return true;

        try {
            const doc = new DOMParser().parseFromString(bodyHtml || "", "text/html");
            // 자동 삽입 서명 + 답장 인용 — 사용자가 쓴 것이 아니다.
            doc.body.querySelectorAll("[data-signature], blockquote").forEach(el => el.remove());
            return (doc.body.textContent || "").replace(/\s| /g, "").length > 0;
        } catch {
            // 파싱이 안 되면 저장 쪽으로 기운다 — 쓴 걸 잃는 쪽이 초안 한 줄보다 나쁘다.
            return Boolean((bodyHtml || "").trim());
        }
    };

    /**
     * 닫기 요청 한 곳 — ✕ 버튼 · ESC · 배드드롭이 모두 여기로 온다.
     *
     * <p>쓰던 내용이 있으면 <b>말없이 사라지게 두지 않는다</b>. 다만 <b>대신 저장해 주지도 않는다</b> —
     * 저장할지 <b>물어보고</b>, "임시저장" 을 고른 경우에만 초안을 남긴다(2026-08-08 요청).
     * 자동으로 남기던 때는 내용이 있는 닫기 한 번이 곧 임시보관함 한 줄이라, 안 쓰기로 하고
     * 접은 메일까지 전부 초안으로 남았다 — 지우는 것도 사용자 몫이 됐다.
     *
     * <p>"저장 안 함" 은 그냥 닫는다. 단, <b>이미 저장해 둔 초안이 있으면 그 행은 그대로 둔다</b> —
     * 여기서 지우지 않는다(초안 삭제는 임시보관함에서 한다. handleDiscardDraft 주석과 같은 이유).
     *
     * <p>{@code closePromptRef} 로 재진입을 막는다. ✕ 를 더블클릭하면 2타가 아직 안 깔린
     * 확인 다이얼로그의 배드드롭 자리에 떨어져 handleRequestClose 가 두 번 돈다. 그대로 두면
     * confirmDialog 의 메아리 가드가 두 번째 호출을 즉시 취소(false)로 응답해
     * <b>묻는 창이 떠 있는데 저장 없이 닫혀 버린다</b>. 답은 첫 호출의 다이얼로그에서만 받는다.
     *
     * <p>저장 실패해도 닫는다. 닫히지 않는 창에 갇히는 편이 더 나쁘고, 실패는 토스트로 알린다.
     */
    const handleRequestClose = async () => {
        if (sending) return;                       // 발송 중엔 닫지 않는다
        if (closePromptRef.current) return;        // 이미 묻고 있다 — 연타로 두 번 묻지 않는다
        if (skipAutoSaveRef.current || !hasUserContent()) { onClose(); return; }

        closePromptRef.current = true;
        let wantSave;
        try {
            wantSave = await confirm({
                title: "임시저장",
                message: [
                    "작성 중인 내용을 임시보관함에 저장할까요?",
                    attachments.length
                        && `일반 첨부 ${attachments.length}개는 임시저장에 포함되지 않습니다.`,
                    large.items.length
                        && `업로드한 대용량 첨부 ${large.items.length}개(${formatBytes(large.totalBytes)})는 `
                           + `임시저장 시 삭제됩니다.`,
                    // 푸터의 '임시저장' 버튼은 단체 모드에서 아예 감춘다(그쪽 주석 참고). 여기서까지
                    // 감추면 쓰던 걸 통째로 잃으므로 저장은 열어 두되, 무엇이 달라지는지는 반드시 말한다.
                    bulkMode
                        && "단체 발송 설정은 저장되지 않습니다 — 다시 열면 받는이 전원이 한 통에 들어간 일반 메일이 됩니다.",
                    "저장하지 않으면 작성 내용은 사라집니다.",
                ].filter(Boolean).join("\n"),
                confirmLabel: "임시저장",
                cancelLabel:  "저장 안 함",
            });
        } finally {
            closePromptRef.current = false;
        }

        if (!wantSave) {
            // "저장 안 함" · ESC · 바깥 클릭.
            //
            // ★ 그냥 닫으면 안 되는 경우가 있다. 대용량 첨부를 붙이면 presign 에 mailId 가 필요해
            //   초안이 '자동으로' 만들어진다. 여기서 그대로 닫으면 사용자는 저장 안 했다고 믿는데
            //   임시보관함에는 2GB 짜리를 문 초안이 남는다 — 넣어두고 잊는 바로 그 상황이 되고,
            //   그게 S3 비용으로 쌓인다.
            //
            //   자동 생성분만 지운다. 임시보관함에서 열어 편집 중이던 '기존' 초안은
            //   사용자가 만든 것이라 '저장 안 함' 이 삭제를 뜻하지 않는다.
            const orphanDraftId = autoCreatedDraftRef.current ? draftIdRef.current : null;
            large.reset();
            if (orphanDraftId) {
                try { await mailApi.largeDeleteUnsent(orphanDraftId); } catch { /* 스윕이 회수 */ }
                try { await mailApi.deleteDraft(orphanDraftId); } catch { /* 닫는 건 막지 않는다 */ }
            }
            onClose();
            return;
        }

        try {
            const savedId = await persistDraft();
            await purgeLargeAfterDraftSave(savedId ?? draftIdRef.current);
            if (savedId) {
                notify(attachments.length
                    ? `임시보관함에 저장했습니다 (첨부 ${attachments.length}개는 저장되지 않습니다)`
                    : "임시보관함에 저장했습니다", "success");
            }
        } catch (e) {
            notify(e?.message || "임시저장에 실패했습니다. 작성 내용이 남아있지 않습니다.", "error");
        } finally {
            onClose();
        }
    };

    /* ── 서명 메뉴 핸들러 ───────────────────────────────
     *
     * <h4>서명 안 이미지 처리</h4>
     * 서명 이미지는 삽입 시점에 전부 base64 data: URL 로 굳힌다 (inlineSignatureImages).
     * 대상은 두 가지 — 인증이 걸린 {@code /api/mail/signatures/images/…} 프록시 URL 과
     * 사내 공개 URL({@code https://onandme.info/…}). 전자는 받는 사람이 우리 인증 엔드포인트에
     * 닿지 못해서, 후자는 Gmail·Outlook 이 수신 메일의 원격 이미지를 기본 차단해서다.
     * 한 번 data: 가 되면 그 다음은 전부 자동 —
     *   - 보낸편지함 뷰어: data: URL DOMPurify 통과 (ADD_DATA_URI_TAGS:['img'])
     *   - 발송: 백엔드 extractInlineImages 가 data: → cid: 자동 변환 → 받는 쪽 정상 표시
     * 판단 기준과 실패 시 폴백은 해당 모듈 주석 참고.
     */
    /**
     * 서명 HTML → 삽입 가능한 안전 HTML 로 변환.
     * 인증 보호 이미지 URL 은 base64 로 inline, <div data-signature> 로 감싸(AI 검수 제외) 뒤 sanitize.
     * 자동 삽입(insertInitialSignature)과 수동 삽입(insertSignatureAtCursor)이 공유한다.
     *
     * <p>pinImageBoxes 는 로고가 안 떠도 자리 폭이 유지되게 만든다 — 명함형 서명은 로고 폭이
     * 곧 오른쪽 열 폭이라, 이미지가 깨지면 열이 좁아져 주소가 접힌다. 자세한 이유는 해당 모듈 주석 참고.
     * 여기(삽입 시점)에 두면 작성기·발송 전 미리보기·실제 발송이 모두 같은 HTML 을 쓰게 된다.
     */
    const prepareSignatureHtml = async (sigHtml) => {
        const inlinedHtml = await inlineSignatureImages(sigHtml);
        const pinned  = pinImageBoxes(inlinedHtml);
        const wrapped = wrapSignature(pinned);  // <div data-signature="1"> 로 감싸 AI 검수 제외
        return DOMPurify.sanitize(wrapped, {
            ADD_ATTR: ["target", "rel", "style", "class", "width", "height", "data-signature"],
            ADD_DATA_URI_TAGS: ["img"],
        });
    };

    const insertSignatureAtCursor = async (sigHtml) => {
        const safe = await prepareSignatureHtml(sigHtml);
        /*
         * 다시보내기엔 Tiptap 이 없다 — 편집 대상은 contentEditable 인 본문(quoteRef) 하나뿐이다.
         * 캐럿이 그 안에 있으면 그 자리에, 아니면(버튼을 누르느라 포커스가 나갔거나 아직 한 번도
         * 클릭하지 않았으면) 맨 끝에 붙인다. 여기서 그냥 return 하면 서명 버튼이 소리 없이 죽는다.
         */
        if (isResendMode) {
            const host = quoteRef.current;
            if (!host) return;
            const sel = window.getSelection();
            const insideQuote = sel && sel.rangeCount > 0
                && host.contains(sel.getRangeAt(0).commonAncestorContainer);
            if (insideQuote) {
                host.focus();
                document.execCommand("insertHTML", false, safe);
            } else {
                host.insertAdjacentHTML("beforeend", safe);
            }
            // DOM 이 진실이지만 state 도 맞춰 둔다 — AI 검토·재오픈이 이 값을 읽는다.
            setQuoteHtmlRaw(host.innerHTML);
            return;
        }
        const editor = editorRef.current;
        if (!editor) return;
        editor.chain().focus().insertContent(safe).run();
    };

    const reloadSignatures = async () => {
        try {
            const res = await mailApi.listSignatures();
            setSignatures((res?.data ?? res) || []);
        } catch { /* noop */ }
    };

    /* ── 본문 이미지 업로드 (인라인) ──────────────────────
     * <p>이전 구현: S3 에 업로드 → /api/mail/signatures/images/{key} 프록시 URL 반환 → 에디터 src 사용.
     * 깨진 X 박스의 원인이 여기 누적됐음:
     *
     *  1) **포트 불일치** — CRA dev 서버(3000) vs API 서버(11115). 반환 URL 이 상대경로라
     *     브라우저는 페이지 origin(3000)에 붙여 요청 → 404. (운영에선 같은 도메인이라 안 보이지만
     *     dev 에서 끊김.)
     *  2) **인증 헤더 누락** — 그 엔드포인트는 X-TOKEN 필요. native <img> 요청은 axios interceptor 가
     *     주입하는 헤더를 못 실음. ?token=… 쿼리로 우회해도 dev 의 포트 문제는 그대로.
     *  3) **외부 수신자 도달 불가** — Gmail/Outlook 받는 쪽은 우리 인증 엔드포인트에 절대 못 닿음.
     *     URL 기반 인라인 이미지는 받는 사람 화면에서 어차피 깨짐.
     *
     * <p>현재 정책: <b>인라인 이미지는 항상 base64 data: URL</b> 로 에디터에 들어감.
     * 백엔드 발송 직전(`MailSendService` → `extractInlineImages`) 에 data: URL 을 자동으로
     * cid: 인라인 첨부(`multipart/related`) 로 변환. 따라서:
     *
     *  - 작성기 — data: URL 이라 네트워크/인증 무관하게 즉시 렌더 (X 박스 X)
     *  - 발송 EML — cid: 로 변환되어 Gmail/Outlook 등 모든 메일 클라이언트 호환
     *  - S3 저장본 — data: URL 그대로 (자기 보낸편지함 보기용. DOMPurify 가
     *    `ADD_DATA_URI_TAGS:['img']` 로 허용하므로 정상 렌더)
     *
     * <p>signature(서명) 이미지는 별도 흐름이라 영향 X — `SignatureEditorDialog` 가
     * 전용으로 `mailApi.uploadSignatureImage` 호출.
     *
     * <p>null 반환 → `RichTextEditor.insertImageFiles` 가 `readFileAsDataUrl` 폴백 사용.
     */
    const handleInlineImageUpload = async () => null;

    return (
        <>
        <Dialog open={open} onClose={handleRequestClose}
             fullWidth
            fullScreen={expanded}
            PaperProps={{ sx: {
                    maxWidth: 1500,
                borderRadius: expanded ? 0 : 1.5,
                height: "100vh", display: "flex", flexDirection: "row", bgcolor: "#fff",
            } }}>
        {/* ── 좌측: 메일 작성 영역 ─
            영역 전체가 첨부 드롭존이다(헤더·본문·푸터 어디든). */}
        <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
                flex: aiOpen ? "0 0 55%" : "1",
                display: "flex", flexDirection: "column", minWidth: 0,
                position: "relative",
            }}>
            {/* 드래그 오버레이 — pointerEvents:none 이라 아래 요소의 dragleave 를 가로채지 않는다.
                (오버레이가 이벤트를 먹으면 나타나는 순간 dragleave 가 나서 즉시 사라진다) */}
            {dragActive && (
                <Box sx={{
                    position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "rgba(37,99,235,0.06)",
                    border: "2px dashed #2563eb", borderRadius: 1.5,
                }}>
                    <Box sx={{
                        px: 3, py: 2, borderRadius: 1.5, bgcolor: "#fff",
                        border: "1px solid #bfdbfe", boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
                        textAlign: "center",
                    }}>
                        <AttachFileRoundedIcon sx={{ fontSize: 28, color: "#2563eb" }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e40af", mt: 0.5 }}>
                            여기에 놓으면 첨부됩니다
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                            폴더째로 놓아도 됩니다 · 첨부 합계 25MB 까지
                        </Typography>
                    </Box>
                </Box>
            )}
            {/* 헤더 */}
            <Box sx={{
                px: 2.5, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", gap: 1,
            }}>
                <Typography noWrap sx={{ fontSize: 15, fontWeight: 700, minWidth: 0, color: "#0f172a", lineHeight: 1.3 }}>
                    {mode === "bulk" ? "단체 메일"
                        : mode === "scheduled" ? "예약 메일"
                        : (initial ? (initial.subject?.startsWith("Re:") ? "답장"
                            : initial.subject?.startsWith("Fwd:") ? "전달" : "메일 작성") : "새 메일 작성")}
                </Typography>
                <ToggleButtonGroup
                    size="small" exclusive
                    value={mode}
                    // 모드 전환은 '어떻게 보낼지'만 바꾼다 — 작성 내용(본문·첨부·AI 검토 패널)은 그대로 유지.
                    // 예전엔 '단체'로 바꾸는 순간 AI 검토 패널을 강제로 닫았는데, 사용자가 하던 검토가
                    // 아무 설명 없이 사라지는 동작이라 제거했다.
                    onChange={(e, v) => { if (v) setMode(v); }}
                    sx={{ ml: 1.5,
                          "& .MuiToggleButton-root": { py: 0.15, px: 1.2, fontSize: 12, textTransform: "none", fontWeight: 700, color: "#64748b" },
                          "& .Mui-selected": { color: "#2563eb !important" } }}>
                    <ToggleButton value="normal">일반 메일</ToggleButton>
                    <ToggleButton value="bulk">단체 메일</ToggleButton>
                    <ToggleButton value="scheduled">예약 메일</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                {/* 창 크기 전환 — 기본은 모달, 좁다고 느끼면 전체화면.
                    라우트를 새로 파지 않고 Dialog 의 fullScreen 만 바꾸는 이유는 이 컴포넌트가
                    언마운트되지 않기 때문이다. 첨부는 서버가 아니라 메모리의 File 객체로 들고
                    있어서(발송 시 한 번에 multipart 로 나간다), 페이지 이동으로 다시 마운트되면
                    담아둔 첨부가 통째로 사라진다. 본문·인용·AI 검토 패널도 마찬가지다. */}
                <Tooltip title={expanded ? "작게 보기" : "창으로 열기 (넓게)"}>
                    <IconButton size="small" onClick={() => setExpanded(v => !v)} sx={{ color: "#64748b" }}>
                        {expanded
                            ? <CloseFullscreenRoundedIcon sx={{ fontSize: 16 }} />
                            : <OpenInFullRoundedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={handleRequestClose} sx={{ color: "#64748b" }}>
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, bgcolor: "#fff" }}>
                {/* 주소·제목 */}
                <Box sx={{ px: 2.5, pt: 1, flexShrink: 0 }}>
                    {/* 받는이·참조·숨은참조 — 세 모드가 동일하다. 모드는 '어떻게 보낼지' 일 뿐이라
                        작성 내용이 모드 전환으로 사라지거나 입력칸이 없어지면 안 된다.
                        단체에서 받는이 라벨만 '개별 발송 대상' 임을 드러내도록 바꾼다. */}
                    {/* 수신 그룹 — 받는이 칸의 지우기(X) 오른쪽에 같은 줄로 붙인다.
                        예전엔 세 칸 위에 한 줄을 통째로 썼는데, 작성창에서 가장 아쉬운 게 세로 공간이라
                        도구 하나가 한 행을 차지할 이유가 없다. '받는이만 채운다' 로 읽힐 위험은
                        버튼 툴팁(받는이·참조·숨은참조를 한 번에)과 적용 후 토스트(어느 칸에 몇 명)가 막는다. */}
                    <RecipientAutocomplete
                        label={bulkMode ? "받는이(개별)" : "받는이"}
                        value={tos} onChange={setTos} autoFocus
                        trailing={
                            <ContactGroupPickerButton
                                tos={tos} ccs={ccs} bccs={bccs}
                                // 병합은 여기서 한다 — 버튼이 '추가할 것' 만 돌려주고, 합치는 건
                                // 최신 값을 아는 setState 업데이터가 맡는다(그 사이 사용자가 친 값과 경합하지 않도록).
                                onApply={({ tos: t, ccs: c, bccs: b }) => {
                                    if (t.length) setTos(prev => [...prev, ...t]);
                                    if (c.length) setCcs(prev => [...prev, ...c]);
                                    if (b.length) setBccs(prev => [...prev, ...b]);
                                }}
                                // 입력 행 높이(27px)에 맞춘 납작한 형태 — 기본 small 버튼은 행을 밀어 올린다.
                                sx={{ py: 0, px: 1, minWidth: 0, height: 26, fontSize: 11.5, whiteSpace: "nowrap" }}
                            />
                        } />
                    <RecipientAutocomplete label="참조"     value={ccs}  onChange={setCcs} />
                    <RecipientAutocomplete label="숨은참조" value={bccs} onChange={setBccs} />
                    <Box sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid #eef2f6", py: 0.5 }}>
                        <Typography sx={{
                            fontSize: 12, fontWeight: 600, color: "#64748b",
                            width: 70, flexShrink: 0,
                        }}>
                            제목
                        </Typography>
                        <TextField
                            fullWidth size="small" variant="standard"
                            placeholder="제목 없음"
                            value={subject} onChange={(e) => setSubject(e.target.value)}
                            InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontWeight: 600 } }}
                        />
                        {/* 제목 추천은 AI 검토 패널 안에서 동시에 처리됨 — 별도 버튼 제거 */}
                    </Box>

                    {/* 예약 시간 — 제목 아래, 받는이·제목과 같은 라벨 그리드에 맞춘 한 행.
                        [기존 문제] 입력칸이 라벨에서 멀리 떨어져 행 오른쪽에 붕 떠 보였고, 정확한 시각을
                        직접 타이핑하는 것 말고는 방법이 없었다. 이제 (1) 입력칸을 라벨 바로 옆에 붙이고
                        (2) 자주 쓰는 시각을 한 번에 채우는 프리셋을 옆에 두어, 흔한 경우는 클릭 한 번으로 끝난다. */}
                    {(scheduledMode || bulkMode) && (
                        <Box sx={{
                            display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 0.5,
                            borderBottom: "1px solid #eef2f6", py: 0.5,
                        }}>
                            <Typography sx={{
                                fontSize: 12, fontWeight: 600, color: "#64748b",
                                width: 70, flexShrink: 0,
                            }}>
                                예약 시간
                            </Typography>
                            <TextField
                                type="datetime-local" size="small" variant="standard"
                                value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                                inputProps={{
                                    // 과거 시각은 아예 고를 수 없게 — 고른 뒤 토스트로 거절당하는 것보다 낫다.
                                    min: toLocalInputValue(new Date(Date.now() + 60 * 1000)),
                                    // datetime-local 은 기본적으로 입력칸 '오른쪽 끝의 작은 달력 아이콘'을
                                    // 정확히 눌러야만 달력이 열린다. 그 과녁이 너무 작고 라벨에서 멀어서,
                                    // 입력칸 아무 데나 눌러도 달력이 열리게 한다. (showPicker 미지원 브라우저는
                                    // 예외를 던지므로 삼키고 기존 동작 — 직접 타이핑 — 으로 자연스럽게 폴백.)
                                    onClick: (e) => { try { e.currentTarget.showPicker?.(); } catch { /* 폴백: 직접 입력 */ } },
                                    style: { cursor: "pointer" },
                                }}
                                InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
                                sx={{ width: 190, flexShrink: 0 }}
                            />
                            <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
                                {SCHEDULE_PRESETS.map(p => (
                                    <Chip
                                        key={p.label}
                                        label={p.label}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setScheduleAt(toLocalInputValue(p.at()))}
                                        sx={{
                                            height: 20, fontSize: 11, fontWeight: 600,
                                            color: "#2563eb", borderColor: "#bfdbfe",
                                            "&:hover": { bgcolor: "#eff6ff", borderColor: "#93c5fd" },
                                        }}
                                    />
                                ))}
                                {scheduleAt && (
                                    <Chip
                                        label="지우기" size="small" variant="outlined"
                                        onClick={() => setScheduleAt("")}
                                        sx={{ height: 20, fontSize: 11, color: "#94a3b8", borderColor: "#e2e8f0" }}
                                    />
                                )}
                            </Stack>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: 1.5, whiteSpace: "nowrap" }}>
                                {bulkMode ? "(선택) 비우면 바로 발송" : "지정한 시각에 자동 발송"}
                            </Typography>
                        </Box>
                    )}

                    {/* 단체 모드 안내 — 받는이·제목 아래 별도 섹션 */}
                    {bulkMode && (
                        <Box sx={{
                            mt: 1, mb: 0.5, px: 1.2, py: 0.9,
                            bgcolor: "#f8fafc", border: "1px solid #eef2f6", borderRadius: 1,
                            display: "flex", alignItems: "flex-start", gap: 0.8,
                        }}>
                            <CampaignRoundedIcon sx={{ fontSize: 16, color: "#2563eb", mt: "1px", flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11.5, color: "#475569", lineHeight: 1.6 }}>
                                받는이 <b style={{ color: "#2563eb" }}>{bulkList.length}명</b>에게 <b>개별 메일 1통씩</b> 발송 —
                                받는이끼리 서로의 주소가 보이지 않습니다. 참조·숨은참조·첨부는 일반 메일과 동일하게 적용됩니다.
                                진행은 <b>예약함 › 단체 발송</b>에서 확인하세요.
                                {(ccs.length > 0 || bccs.length > 0) && (
                                    <Box component="span" sx={{ display: "block", mt: 0.4, color: "#b45309", fontWeight: 600 }}>
                                        ※ 참조·숨은참조는 <b>한 통마다</b> 붙으므로 {ccs.length + bccs.length}명이 각각 {bulkList.length}통을 받게 됩니다.
                                        한 통만 받게 하려면 <b>일반 메일</b>로 보내세요.
                                    </Box>
                                )}
                                <Box component="span" sx={{ display: "block", mt: 0.4, color: "red", fontWeight: 600 }}>
                                    ※ 단체메일은 임시저장이 되지 않습니다. 작성에 주의하세요
                                </Box>
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 본문 영역 — Tiptap + 인용을 한 외곽 박스 안에 통합.
                    인용 있을 때: Tiptap 의 자체 border 제거 (noOuterBorder=true) +
                    외곽 wrapper 하나만 border 가짐 → 한 영역처럼 보임. */}
                <Box sx={{
                    flex: 1, p: 2.5,
                    display: "flex", flexDirection: "column",
                    bgcolor: "#fff", minHeight: 0,
                }}>
                    {/* 통합 외곽 컨테이너 — 인용 있을 때만 border. 안에 Tiptap + 인용 함께 스크롤.
                        본문이 빈 메일을 다시보내기 하면 quoteHtmlRaw 가 빈 문자열이지만 그 경우에도
                        편집 영역은 있어야 한다(유일한 입력 칸이므로) → isResendMode 를 함께 본다. */}
                    <Box sx={{
                        flex: 1, minHeight: 0,
                        display: "flex", flexDirection: "column",
                        overflowY: "auto",
                        ...((quoteHtmlRaw || isResendMode) ? {
                            border: "1px solid #e2e8f0",
                            borderRadius: 1.5,
                            bgcolor: "#fff",
                            "&:focus-within": {
                                borderColor: "#2563eb",
                                boxShadow: "0 0 0 2px rgba(37,99,235,0.12)",
                            },
                            transition: "border-color 0.15s, box-shadow 0.15s",
                        } : {}),
                    }}>
                        {/* 사용자 메시지 — Tiptap. 인용이 있을 때 자체 border 제거해서 외곽 wrapper 와 통합.
                            인용 없을 때는 wrapper 의 가용 세로 공간을 모두 차지하도록 flex:1 (안 그러면 자연 콘텐츠 높이만큼만 잡혀 모달 절반만 차지).

                            다시보내기는 이 칸을 통째로 뺀다 — 원본을 그대로 한 번 더 보내는 것이라 위에 쓸
                            내용이 없는데, RichTextEditor 의 기본 minHeight(240) + padding(32) 이 본문 위
                            272px 짜리 빈 칸으로 보였다(위 isResendMode 주석). 서식 툴바도 함께 사라진다. */}
                        {!isResendMode && (
                        <Box sx={{
                            ...(quoteHtmlRaw
                                ? { flexShrink: 0, minHeight: 100 }
                                : { flex: 1, minHeight: 0 }),
                            display: "flex", flexDirection: "column",
                        }}>
                            <TiptapBodyEditor
                                value={bodyHtml}
                                onChange={setBodyHtml}
                                onTextChange={setBodyText}
                                editorKey={editorKey}
                                onUploadImage={handleInlineImageUpload}
                                onEditorReady={(editor) => { editorRef.current = editor; }}
                                noOuterBorder={!!quoteHtmlRaw}
                                // 본문 위에 떨어뜨린 파일도 '첨부'로 간다 — 작성 영역 전체가 드롭존이다.
                                // 켜지 않으면 이미지가 인라인 + 첨부 두 벌로 들어간다(handleDrop 주석 참고).
                                disableImageDrop
                                // 메일 본문 @ 멘션 자동완성 사용 안 함 — 메일 발송 자체가 받는이/참조에게 통지
                                // 역할을 하므로 본문에 별도 멘션 알림 토큰이 불필요. (mentionFetcher 미설정)
                            />
                        </Box>
                        )}

                        {/* 인용 블록 — 본문과 한 흐름으로 이어짐. contentEditable 로 사용자 편집 가능.

                            다시보내기에서는 이게 인용이 아니라 '본문 그 자체' 다. 위 칸이 없으므로
                            flexGrow 로 남은 세로 공간을 채워 클릭할 곳이 넉넉하게 하고(짧은 메일에서
                            좁은 띠처럼 보이지 않게), flexShrink:0 을 유지해 내용이 길면 컨테이너가
                            스크롤되게 둔다. 여백도 에디터와 같은 16/18px 로 맞춰 위치가 튀지 않게 한다. */}
                        {(quoteHtmlRaw || isResendMode) && (
                            <Box
                                ref={quoteRef}
                                contentEditable
                                suppressContentEditableWarning
                                sx={{
                                    flexShrink: 0,
                                    outline: "none",
                                    ...(isResendMode
                                        ? { flexGrow: 1, minHeight: 240, px: 2.25, py: 2 }
                                        : { minHeight: 40, px: 1.5, py: 1 }),
                                    "& img": { maxWidth: "100%", height: "auto" },
                                    "& table": { maxWidth: "100%" },
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* 첨부 */}
                {attachments.length > 0 && (
                    <Box sx={{ px: 2.5, pb: 1.5, flexShrink: 0 }}>
                        <AttachmentDropzone
                            attachments={attachments}
                            onRemove={handleAttachmentRemove}
                            mode="display"
                        />
                    </Box>
                )}

                {/*
                  대용량 첨부 — 25MB 를 넘겨 EML 에 담을 수 없는 파일. 목록을 분리한 이유는
                  보여줄 정보가 다르기 때문이다(업로드 진행률 · 다운로드 가능기간 · 재시도).
                */}
                {large.items.length > 0 && (
                    <Box sx={{ px: 2.5, pb: 1.5, flexShrink: 0 }}>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                대용량 첨부 {large.items.length}개 · {formatBytes(large.totalBytes)}
                            </Typography>
                            {/* 잔여 용량 — 2GB 파일을 고르기 '전에' 들어갈 자리가 있는지 알아야 한다.
                                업로드를 30분 돌린 뒤 한도 초과로 거부되면 그 시간이 통째로 낭비된다. */}
                            {large.quota && (
                                <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                                    이 메일 {formatBytes(large.quota.mailUsed)}/{formatBytes(large.quota.mailLimit)}
                                    {" "}({large.quota.mailCount}/{large.quota.mailMaxCount}개)
                                    {" · "}내 보관 {formatBytes(large.quota.userUsed)}/{formatBytes(large.quota.userLimit)}
                                </Typography>
                            )}
                        </Stack>
                        <LargeAttachmentList
                            items={large.items}
                            quota={large.quota}
                            onRemove={large.remove}
                            onRetry={large.retry}
                        />
                    </Box>
                )}
            </DialogContent>

            {/* 푸터 */}
            <Box sx={{
                px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", gap: 1, bgcolor: "#fafbfc",
            }}>
                <AttachmentDropzone
                    onFilesAdded={handleAttachmentsAdded}
                    mode="trigger"
                    button={
                        <FooterAction
                            component="span"
                            icon={<AttachFileRoundedIcon sx={{ fontSize: 18 }} />}
                            label="첨부"
                            tooltip="첨부파일 추가"
                        />
                    }
                />
                {/* 임시저장 — 이 버튼과 닫을 때의 확인, 두 경로. 자동저장(주기·이탈)은 되살리지 않는다.

                    단체 메일에서 감추는 이유: persistDraft 는 sendMode 를 담지 않는다. 단체로 쓰던 걸
                    저장하면 받는이 N명이 그대로 tos 에 들어간 '일반 메일' 초안이 되고, 그걸 다시 열어
                    보내면 N명이 서로의 주소를 보게 된다. 개별 발송이던 것이 단체 참조로 바뀌는 사고다.

                    "첨부는 안 담긴다" 는 툴팁으로 두되, 첨부가 실제로 있으면 handleSaveDraft 가
                    개수를 세어 확인을 받는다 — 마우스를 올려 봐야 아는 정보에 기대지 않는다.
                    발송 시점에야 첨부가 없어진 걸 알아채면 그게 진짜 사고다. */}
                {!bulkMode && (
                    <FooterAction
                        onClick={handleSaveDraft}
                        disabled={savingDraft}
                        icon={<SaveRoundedIcon sx={{ fontSize: 18 }} />}
                        label={savingDraft ? "저장 중" : "임시저장"}
                        tooltip="제목·받는이·본문만 임시보관함에 저장합니다 (첨부 제외)"
                    />
                )}
                <FooterAction
                    onClick={(e) => setPickerAnchor(e.currentTarget)}
                    icon={<EditNoteRoundedIcon sx={{ fontSize: 20 }} />}
                    label="서명"
                    tooltip="서명 메뉴"
                />
                <FooterAction
                    onClick={handleDiscardDraft}
                    color="#94a3b8"
                    icon={<DeleteRoundedIcon sx={{ fontSize: 18 }} />}
                    label="폐기"
                    tooltip="작성 폐기 — 임시저장 없이 닫습니다"
                />

                <Box sx={{ flex: 1 }} />

                {/* AI 검토 — 발송 방식과 무관하게 항상 제공한다.
                    [기존 문제] 단체 모드에선 이 버튼 자체가 렌더링되지 않았고, 모드를 '단체'로 바꾸면
                    열려 있던 패널까지 강제로 닫혔다. 정작 수십 명에게 한꺼번에 나가는 단체 메일이야말로
                    문장을 가장 신중하게 다듬어야 하는 경우다. 검토 대상은 제목·본문이라 세 모드가 동일하다. */}
                <ActionButton
                    variant={aiOpen ? "primary" : "secondary"}
                    startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                        // 다시보내기는 사용자가 친 내용이 DOM 에만 있다 — 패널을 열기 직전에
                        // 스냅샷해야 AI 가 '지금 화면의 본문' 을 검토한다.
                        if (!aiOpen && isResendMode && quoteRef.current) {
                            setQuoteHtmlRaw(quoteRef.current.innerHTML);
                        }
                        setAiOpen(v => !v);
                    }}
                    label="AI 검토"
                    tooltip={aiOpen ? "AI 검토 패널을 닫습니다" : "AI 가 메일을 공공기관 톤으로 다듬어 줍니다"}
                    sx={aiOpen
                        ? { bgcolor: "#7c3aed", color: "#fff", "&:hover": { bgcolor: "#6d28d9" } }
                        : { borderColor: "#7c3aed", color: "#7c3aed",
                            "&:hover": { borderColor: "#6d28d9", bgcolor: "#f5f3ff" } }}
                />

                {/* 발송 버튼 — 라벨·아이콘만 모드별로 다르고, 실제 경로는 셋 다 통합 dispatch 하나다. */}
                <Tooltip title={aiOpen ? "AI 검토를 끝내주세요" : ""}>
                    <span>
                        <ActionButton
                            variant="primary"
                            startIcon={
                                bulkMode ? <CampaignRoundedIcon sx={{ fontSize: 16 }} />
                                    : scheduledMode ? <ScheduleSendRoundedIcon sx={{ fontSize: 16 }} />
                                    : <SendRoundedIcon sx={{ fontSize: 16 }} />
                            }
                            onClick={mode === "normal" ? handleSend : handleQuickDispatch}
                            disabled={sending || aiOpen}
                            label={
                                sending
                                    // sendPhase 가 있으면 실제 단계를 보여준다 — 업로드 구간만 %가 실측값이고,
                                    // 그 뒤 서버 처리 구간은 pct=null 이라 문구만 바뀐다(가짜 진행률을 만들지 않는다).
                                    ? (sendPhase
                                        ? (sendPhase.pct != null
                                            ? `${sendPhase.label} ${sendPhase.pct}%`
                                            : `${sendPhase.label}...`)
                                        : (bulkMode ? "등록 중..." : scheduledMode ? "예약 중..." : "보내는 중..."))
                                    : bulkMode
                                        ? `${bulkList.length}명에게 ${scheduleAt ? "예약 발송" : "개별 발송"}`
                                        : scheduledMode ? "예약 발송" : "보내기"
                            }
                            sx={{ "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}
                        />
                    </span>
                </Tooltip>
            </Box>
        </Box>

        {/* ── 우측: AI 검토 패널 — 열려 있을 때만 ─ */}
        {aiOpen && (
            <AiReviewPanel
                subject={subject}
                // 다시보내기는 본문이 quoteHtmlRaw 쪽에 있다 — bodyHtml 은 늘 비어 있어
                // 그대로 넘기면 AI 가 검토할 게 없다. (여는 시점에 DOM → state 로 스냅샷)
                originalHtml={isResendMode ? quoteHtmlRaw : bodyHtml}
                onClose={() => setAiOpen(false)}
                onApplySubject={(s) => {
                    setSubject(s);
                    notify(`제목을 "${s}" 로 교체했습니다`, "success");
                }}
                onApply={(revisedHtml) => {
                    // 다시보내기 — 반영 대상은 Tiptap 이 아니라 contentEditable 본문이다.
                    // DOM 을 직접 갈아끼우고 state 도 맞춘다(주입 useEffect 는 editorKey 에만 걸려 있어
                    // state 변경만으로는 화면이 바뀌지 않는다).
                    if (isResendMode) {
                        if (quoteRef.current) quoteRef.current.innerHTML = revisedHtml || "";
                        setQuoteHtmlRaw(revisedHtml || "");
                        setAiOpen(false);
                        notify("AI 검토 결과를 본문에 반영했습니다", "success");
                        return;
                    }
                    // AI 가 반환한 HTML 은 원본 구조(이미지·표·링크) 그대로,
                    // 텍스트 노드만 새 내용으로 교체된 상태. Tiptap 에 그대로 set 하면 끝.
                    const editor = editorRef.current;
                    if (editor) {
                        editor.commands.setContent(revisedHtml || "<p></p>", { emitUpdate: false });
                        setBodyHtml(editor.getHTML());
                        setBodyText(editor.getText());
                    } else {
                        setBodyHtml(revisedHtml || "<p></p>");
                    }
                    setAiOpen(false);
                    notify("AI 검토 결과를 본문에 반영했습니다", "success");
                }}
            />
        )}
        </Dialog>

        {/* 서명 메뉴: [저장된 서명들] / 보관함 / 서명 설정 */}
        <SignaturePickerMenu
            anchorEl={pickerAnchor}
            open={Boolean(pickerAnchor)}
            onClose={() => setPickerAnchor(null)}
            signatures={signatures}
            onPick={(sig) => { insertSignatureAtCursor(sig.bodyHtml); setPickerAnchor(null); }}
            onOpenGallery={() => { setPickerAnchor(null); setGalleryOpen(true); }}
            onOpenEditor={() => { setPickerAnchor(null); setEditorDlg({}); }}
        />

        {/* 서명 보관함 (전체 목록 + 미리보기 + 기본설정/삭제/편집) */}
        <SignatureGalleryDialog
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            signatures={signatures}
            onReload={reloadSignatures}
            onEdit={(sig) => setEditorDlg(sig)}
            onCreate={() => setEditorDlg({})}
        />

        {/* 서명 편집기 (기본 텍스트 / HTML 소스) */}
        <SignatureEditorDialog
            open={Boolean(editorDlg)}
            initial={editorDlg}
            onClose={() => setEditorDlg(null)}
            onSaved={async () => {
                await reloadSignatures();
                setEditorDlg(null);
            }}
        />

        {/* 발송 후 외부 주소 → 새 연락처 등록 prompt — "+" 버튼이 즉시 등록까지 처리 */}
        <RegisterContactsPrompt
            open={Boolean(registerPrompt)}
            candidates={registerPrompt || []}
            onClose={closeRegisterPrompt}
        />

        {/*
          ── 발송 전 미리보기 다이얼로그 ─────────────────────────────────────
          "보내기" 클릭 시 즉시 발송하지 않고 이 모달을 띄워 사용자가:
            1) 받는 이/제목/본문이 의도대로인지 마지막 점검
            2) AI 첨부 감지 결과(첨부 언급 vs 실제 첨부 유무) 확인
            3) [수정] 으로 작성기 복귀 또는 [최종 발송] 으로 실제 SES 호출
          를 결정하게 한다.
        */}
        <SendPreviewDialog
            open={previewOpen}
            payload={previewPayload}
            largeBlockHtml={previewLargeBlock}
            attachments={attachments}
            aiAttachWarn={aiAttachWarn}
            sending={sending}
            sendPhase={sendPhase}
            onCancel={() => {
                setPreviewOpen(false); setPreviewPayload(null);
                setPreviewLargeBlock(""); setAiAttachWarn(null);
            }}
            onConfirm={handleFinalSend}
            onAddAttachment={() => {
                // 미리보기 닫고 작성기로 복귀해서 첨부 추가하도록 — 클립 아이콘으로 시선 유도
                setPreviewOpen(false);
                notify("좌하단 📎 버튼으로 첨부를 추가해주세요", "info");
            }}
        />
        </>
    );
}

/* ──────────────────────────────────────────────────────────────────
 *  SendPreviewDialog — 발송 전 최종 미리보기.
 *
 *  - 받는 이가 받게 될 메일 본문을 같은 sanitize 정책으로 렌더링해 그대로 보여줌
 *  - AI 첨부 감지 결과가 "본문에 첨부 언급 있는데 실제 첨부 0" 이면 빨간 배너로 경고
 *  - 첨부가 있으면 파일명·크기 리스트도 같이 노출
 *  - [수정] / [최종 발송] 두 가지 결정만 제공 (복잡한 옵션 X — 사용자 confidence 확보가 목적)
 *  - "최종 발송" 이후 발송이 끝날 때까지 이 다이얼로그가 그대로 떠 있으므로,
 *    발송 진행 단계({@code sendPhase})도 여기서 보여준다(아래 푸터 주석 참조).
 *  ────────────────────────────────────────────────────────────── */
function SendPreviewDialog({
    open, payload, largeBlockHtml, attachments, aiAttachWarn,
    sending, sendPhase, onCancel, onConfirm, onAddAttachment,
}) {
    if (!payload) return null;

    // 본문 — payload.bodyHtml 은 이미 buildPreviewPayload 에서 사용자 본문에만 normalize 적용 후
    // raw 인용 블록(quoteHtmlRaw)을 그대로 이어붙인 결과. 여기서 다시 normalize 를 돌리면
    // 인용 안의 외부 광고 메일 spacer/layout 표에까지 검정 border 가 강제되어 wireframe 처럼 보임.
    // 인용은 원본 그대로 보존하는 게 표준 메일 클라이언트 동작과 일치 → 중복 normalize 제거.
    // sanitize 정책은 MailDetail viewer 와 동일 (style·data: img·표 속성 보존).
    // 대용량 첨부 블록은 여기서만 이어붙인다 — payload 는 발송 내용 그대로여야 한다.
    // (서버가 발송 시점에 같은 블록을 주입하므로, payload 에 섞으면 본문에 두 번 들어간다.)
    const safeHtml = DOMPurify.sanitize((payload.bodyHtml || "") + (largeBlockHtml || ""), {
        ADD_ATTR: [
            "target", "rel", "style", "class",
            "colspan", "rowspan", "colwidth",
            "cellpadding", "cellspacing", "border",
            "width", "height",
            "align", "valign", "bgcolor",
        ],
        ADD_TAGS: ["colgroup", "col"],
        ADD_DATA_URI_TAGS: ["img"],
    });

    // 첨부 언급 있는데 실제 첨부 0 → 사용자에게 강한 경고
    const hasAttachWarn = Boolean(
        aiAttachWarn && aiAttachWarn.mentionsAttachment && (attachments?.length ?? 0) === 0
    );

    // 진행 문구 — 첨부 업로드 구간만 %가 실측값(브라우저가 아는 값)이고,
    // 그 뒤 서버 구간(S3 저장 → EML 조립 → SES 발송)은 관측 수단이 없어 문구만 바뀐다.
    const phaseText = sendPhase
        ? (sendPhase.pct != null ? `${sendPhase.label} ${sendPhase.pct}%` : `${sendPhase.label}...`)
        : "보내는 중...";


    const RecipientLine = ({ label, list }) => {
        if (!list || list.length === 0) return null;
        return (
            <Box sx={{ display: "flex", alignItems: "flex-start", py: 0.4 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", width: 64, flexShrink: 0, pt: 0.4 }}>
                    {label}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {list.map((s, i) => (
                        <Chip key={i} label={s} size="small"
                            sx={{
                                fontSize: 11, height: 22, fontWeight: 500,
                                bgcolor: "#eff6ff", color: "#1e40af",
                            }}/>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        // 발송 중 backdrop/ESC 로 닫히지 않게 한다 — 닫혀도 요청은 계속 날아가는데
        // 화면만 사라져 사용자는 "취소됐다" 고 오해한다(실제로는 발송된다).
        <Dialog open={open} onClose={sending ? undefined : onCancel} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 1.5, maxHeight: "92vh" } }}>
            <Box sx={{
                px: 2.5, py: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff",
                display: "flex", alignItems: "center", gap: 1,
            }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, flex: 1, color: "#0f172a" }}>
                    발송 전 미리보기 — 받는 이에게 이렇게 보내집니다
                </Typography>
                <IconButton size="small" onClick={onCancel} disabled={sending} sx={{ color: "#64748b" }}>
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
                {/* AI 검수 progress 배너 제거됨 — 클라이언트 키워드 체크라 즉시 결과. 별도 progress 불필요. */}

                {/* ── 첨부 누락 감지 경고 배너 (클라이언트 키워드 매칭) ─ */}
                {hasAttachWarn && (
                    <Box sx={{
                        mx: 2.5, mt: 2, p: 1.5,
                        bgcolor: "#fee2e2", border: "1px solid #fecaca", borderRadius: 1.5,
                    }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#991b1b", mb: 0.4 }}>
                            ⚠ 본문에 첨부 파일을 언급했는데, 실제 첨부가 없습니다
                        </Typography>
                        {aiAttachWarn.evidence?.length > 0 && (
                            <Box sx={{ pl: 1.5, mt: 0.5 }}>
                                {aiAttachWarn.evidence.map((q, i) => (
                                    <Typography key={i} sx={{ fontSize: 12, color: "#7f1d1d", lineHeight: 1.5 }}>
                                        • "{q}"
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        <Box sx={{ mt: 1 }}>
                            <ActionButton
                                variant="secondary"
                                startIcon={false}
                                onClick={onAddAttachment}
                                label="작성으로 돌아가 첨부 추가"
                                sx={{
                                    fontSize: 12,
                                    borderColor: "#dc2626", color: "#b91c1c",
                                    "&:hover": { borderColor: "#b91c1c", bgcolor: "#fee2e2" },
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* ── 메타 정보 — 받는 이/참조/숨은참조/제목 ─ */}
                <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #eef2f6" }}>
                    <RecipientLine label="받는 이" list={payload.tos} />
                    <RecipientLine label="참조"     list={payload.ccs} />
                    <RecipientLine label="숨은참조" list={payload.bccs} />
                    <Box sx={{ display: "flex", alignItems: "baseline", pt: 0.6 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", width: 64, flexShrink: 0 }}>
                            제목
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                            {payload.subject || "(제목 없음)"}
                        </Typography>
                    </Box>
                </Box>

                {/* ── 본문 — 받는 이 화면과 동일하게 렌더 ─ */}
                {/*
                  표 잘림 방지 — Tiptap 표는 셀에 colwidth/inline width 가 들어가 있어
                  컨테이너 폭을 넘는 경우 우측이 잘려 보이는 문제 있음. 본문 영역 자체를
                  overflow-x:auto 로 두면 표가 넘칠 때만 가로 스크롤바가 생겨 사용자가
                  전체를 볼 수 있다 (Gmail/Outlook 도 같은 방식).
                */}
                <Box sx={{ px: 2.5, py: 2, overflowX: "auto" }}>
                    {safeHtml ? (
                        <Box
                            sx={{
                                fontSize: 14, color: "#1e293b", lineHeight: 1.55,
                                "& > *:first-of-type": { mt: 0 },
                                "& > *:last-of-type":  { mb: 0 },
                                "& p":  { m: 0, mb: 0.4 },
                                "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 1.2, mb: 0.6 },
                                "& ul, & ol": { mt: 0.4, mb: 0.4, pl: 3 },
                                "& li":  { mt: 0, mb: 0.2 },
                                "& a":     { color: ds.colors.primary },
                                "& img":   { maxWidth: "100%", height: "auto" },
                                "& blockquote": {
                                    borderLeft: "3px solid #cbd5e1", pl: 2, ml: 0, my: 0.6,
                                    color: "#64748b",
                                },
                                // 표 — Tiptap 의 colwidth (셀 inline width) 를 존중하되 셀 내용이
                                // 길어 폭을 넘기면 word-break 로 줄바꿈해서 컨테이너 안에 맞춤.
                                // 그래도 못 들어가면 부모의 overflow-x:auto 가 가로 스크롤로 처리.
                                // !important 로 inline style 의 border:none 등을 덮어씀.
                                //
                                // :not([data-signature] …) 로 서명은 제외한다.
                                //   !important 는 inline style 까지 이긴다. 그래서 서명처럼 인라인으로
                                //   모양을 잡는 표(빨간 밑줄 한 줄 + 테두리 없음)에 이 규칙이 닿으면
                                //   밑줄이 검정으로 덮이고 사방에 격자가 생겨 실제 발송본과 딴판이 된다.
                                //   "받는 이에게 이렇게 보내집니다" 가 이 모달의 약속이므로 어긋나면 안 된다.
                                //   여기서 되돌리는 대신 아예 매칭에서 빼는 이유 —
                                //   되돌리려면 border:none !important 가 필요한데 그러면 이번엔
                                //   서명의 빨간 밑줄까지 같이 지워진다.
                                // [data-gaya-large-attach] 도 제외한다 — 서명과 같은 이유다.
                                //   이 블록은 인라인 style 로 모양을 잡는데(연회색 카드 + 파란 밑줄 링크),
                                //   여기 !important 가 닿으면 사방에 검정 격자가 생겨 실제 발송본과 딴판이 된다.
                                "& table:not([data-signature] table):not([data-gaya-large-attach] table)": {
                                    borderCollapse: "collapse !important",
                                    my: 0.6, maxWidth: "100%", tableLayout: "auto",
                                    border: "1px solid #000 !important",
                                },
                                "& td:not([data-signature] td):not([data-gaya-large-attach] td), & th:not([data-signature] th):not([data-gaya-large-attach] th)": {
                                    border: "1px solid #000 !important",
                                    p: 1, verticalAlign: "top",
                                    wordBreak: "break-word",   // "111111입니다" 처럼 공백 없는 긴 토큰도 줄바꿈
                                    overflowWrap: "anywhere",  // 어디서든 줄바꿈 허용 (CJK 친화)
                                },
                                "& th:not([data-signature] th)": { backgroundColor: "#f8fafc", fontWeight: 600 },
                                // 서명 표가 모달 폭을 넘기면 부모의 overflow-x:auto 로 넘긴다.
                                "& [data-signature] table": { maxWidth: "100%" },
                            }}
                            dangerouslySetInnerHTML={{ __html: safeHtml }}
                        />
                    ) : (
                        <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>(본문 없음)</Typography>
                    )}
                </Box>

                {/* ── 첨부 ─ */}
                {attachments && attachments.length > 0 && (
                    <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #eef2f6", bgcolor: "#fafbfc" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 0.6 }}>
                            첨부 {attachments.length}개
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                            {attachments.map(a => (
                                <Chip key={a.id} label={`${a.filename} · ${formatBytes(a.sizeBytes)}`}
                                    size="small"
                                    sx={{
                                        fontSize: 11.5, height: 24,
                                        bgcolor: "#fff", border: "1px solid #e2e8f0",
                                    }}/>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            {/* ── 푸터 — [수정] / [최종 발송] ─ */}
            <Box sx={{
                px: 2.5, py: 1.5, borderTop: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", gap: 1, bgcolor: "#fff",
            }}>
                {/*
                  ── 발송 진행 표시 ─
                  "최종 발송" 을 누른 뒤에도 이 다이얼로그는 성공 시점까지 그대로 떠 있다(performDispatch 가
                  성공 후에야 setPreviewOpen(false) 한다). 그래서 진행 단계는 반드시 여기 있어야 한다.
                  예전엔 작성창 '보내기' 버튼 라벨에만 단계를 그렸는데, 그 버튼은 이 다이얼로그에 덮여
                  있어서 즉시 발송에서는 사용자가 단계를 전혀 볼 수 없었다(예약·단체만 보였다).

                  막대는 아는 만큼만 그린다 — 첨부 업로드 구간은 determinate(실측 %),
                  서버 처리 구간은 indeterminate. 가짜 진행률은 만들지 않는다.
                */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {sending && (
                        <>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.5 }}>
                                {phaseText}
                            </Typography>
                            <LinearProgress
                                variant={sendPhase?.pct != null ? "determinate" : "indeterminate"}
                                value={sendPhase?.pct ?? 0}
                                sx={{
                                    height: 6, borderRadius: 3, bgcolor: "#e2e8f0",
                                    "& .MuiLinearProgress-bar": { borderRadius: 3 },
                                }}
                            />
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                                발송이 끝날 때까지 창을 닫지 마세요
                            </Typography>
                        </>
                    )}
                </Box>
                <ActionButton variant="secondary" onClick={onCancel} disabled={sending} startIcon={false} label="수정" />
                <ActionButton
                    variant="primary"
                    startIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={onConfirm}
                    disabled={sending}
                    label={sending ? phaseText : hasAttachWarn ? "그래도 발송" : "최종 발송"}
                    sx={hasAttachWarn ? {
                        bgcolor: "#dc2626",
                        "&:hover": { bgcolor: "#b91c1c" },
                    } : undefined}
                />
            </Box>
        </Dialog>
    );
}
